<!--
  PlatformSettings -- SuperAdmin platform settings surface.

  _No card -- Super Admin platform settings surface (Settings tab + first
  toggle stub)._

  The future home for platform-wide feature switches + global settings
  (default timezone, etc.). This first cut ships real, persisted
  settings so the surface is production-shaped rather than a mock.

  Setting 1: "Hide color variants we can't generate previews for".
  Persists to the platform_settings key
  ``hide_unpreviewable_color_variants`` (boolean, default TRUE) via:

    * GET   /api/platform-settings/hide-unpreviewable-color-variants
        -> {"value": <bool>}  (login_required)
    * PATCH /api/platform-settings/hide-unpreviewable-color-variants
        body {"value": <bool>}  (superadmin.settings)

  ENFORCED (2026-07-30): the product editor reads this toggle and gates
  its Artifi coverage hiding on it. When ON (the default), unpreviewable
  color variants are hidden from the editor's Colors + Supplier-SKU
  panels; when OFF, those colors are shown dimmed/flagged instead of
  hidden. The default is TRUE so the launch behavior (hide) is preserved
  until an admin opts out.

  Toggle markup + CSS reuse the .sa-toggle switch pattern from
  PlansPricing.vue's is_public toggle.

  Setting 2: "Allowed decoration methods" -- card "Emb + DTF only
  (decoration-method filter)" https://trello.com/c/GUp9ybSQ. Persists to
  the platform_settings key ``allowed_decoration_method_slugs`` (a JSON
  array of DecorationProcess slugs, default ["embroidery", "dtf"]) via:

    * GET   /api/platform-settings/allowed-decoration-method-slugs
        -> {"value": [<slug>, ...]}  (login_required)
    * PATCH /api/platform-settings/allowed-decoration-method-slugs
        body {"value": [<slug>, ...]}  (superadmin.settings)

  ENFORCED: the ``get_decoration_processes`` picker endpoint (backing
  every method-picker surface in the app) filters by this allowlist, so
  narrowing/widening here takes effect immediately with no deploy. The
  checkbox group is fetched from ``GET /api/decoration_processes/`` --
  but see the note below on why that list is fetched WITHOUT the
  allowlist filter for this admin surface specifically. An admin cannot
  uncheck the LAST remaining method (an empty allowlist is rejected by
  the backend and is a footgun in the UI too).

  data-testid: sa-settings-page (root), sa-setting-hide-unpreviewable-toggle
  (the switch input), sa-setting-decoration-methods (methods card),
  sa-setting-decoration-method-<slug> (each checkbox).
-->
<template>
    <section class="sa-settings" data-testid="sa-settings-page">
        <header class="sa-settings__header">
            <h1 class="sa-settings__title">Settings</h1>
            <p class="sa-settings__lede">
                Platform-wide feature switches and global defaults. New
                settings land here as they come online.
            </p>
        </header>

        <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            color="primary"
            size="32"
            width="4"
            class="mt-4"
            data-testid="sa-settings-loading"
        />

        <template v-else>
            <div class="sa-settings__card" data-testid="sa-setting-hide-unpreviewable">
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-hide-unpreviewable-toggle"
                        >
                            Hide color variants we can't generate previews for
                        </label>
                        <p class="sa-settings__setting-help">
                            When on, color variants Artifi cannot preview are
                            hidden from the product editor. When off, they are
                            shown dimmed and flagged instead of hidden.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-hide-unpreviewable-toggle"
                            v-model="hideUnpreviewable"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-hide-unpreviewable-toggle"
                            :disabled="saving"
                            @change="onToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ hideUnpreviewable ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-hide-unpreviewable-note"
                >
                    On by default. The product editor hides unpreviewable color
                    variants while this is on; turn it off to show them dimmed
                    and flagged instead.
                </p>

                <p
                    v-if="errorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-hide-unpreviewable-error"
                >
                    {{ errorMsg }}
                </p>
            </div>

            <div class="sa-settings__card" data-testid="sa-setting-suppress-snackbars">
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-suppress-snackbars-toggle"
                        >
                            Suppress error snackbars
                        </label>
                        <p class="sa-settings__setting-help">
                            When on, API error toasts are silently dropped
                            globally. Use during demos or testing to prevent
                            noisy error banners from appearing. Off by default.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-suppress-snackbars-toggle"
                            v-model="suppressSnackbars"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-suppress-snackbars-toggle"
                            :disabled="snackbarSaving"
                            @change="onSnackbarToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ suppressSnackbars ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-suppress-snackbars-note"
                >
                    Off by default. Turn on temporarily for demos; remember to
                    turn it off afterward so errors surface normally.
                </p>

                <p
                    v-if="snackbarErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-suppress-snackbars-error"
                >
                    {{ snackbarErrorMsg }}
                </p>
            </div>

            <div
                class="sa-settings__card"
                data-testid="sa-setting-disable-artwork-gates"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-disable-artwork-gates-toggle"
                        >
                            Disable artwork launch gates
                        </label>
                        <p class="sa-settings__setting-help">
                            Turns off two store-launch checks: artwork
                            digitization, and client approval of artwork. When
                            on, a store can be launched while its decorations
                            are still being digitized or are still awaiting
                            approval. For end-to-end testing, so a test store
                            is not blocked waiting on a digitizer or an
                            approver. The shipping-address check is NOT
                            affected and still blocks a launch.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-disable-artwork-gates-toggle"
                            v-model="disableArtworkGates"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-disable-artwork-gates-toggle"
                            :disabled="artworkGatesSaving"
                            @change="onArtworkGatesToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ disableArtworkGates ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-disable-artwork-gates-note"
                >
                    Off by default. Turn on temporarily for testing; remember
                    to turn it back off afterward, or a real store can go live
                    with artwork nobody has approved.
                </p>

                <p
                    v-if="artworkGatesErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-disable-artwork-gates-error"
                >
                    {{ artworkGatesErrorMsg }}
                </p>
            </div>

            <div
                class="sa-settings__card"
                data-testid="sa-setting-disable-store-gates"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-disable-store-gates-toggle"
                        >
                            Disable store launch gates
                        </label>
                        <p class="sa-settings__setting-help">
                            Turns off ALL of the account checks that must pass
                            before a store can go live: company details
                            complete, Stripe able to take card payments, sales
                            tax states reviewed, the Master Services
                            Agreement signed, and at least one supplier
                            connected. When
                            on, any store can be launched whatever state its
                            account is in; Review &amp; Launch still lists the
                            unmet checks as warnings. Stores that are already
                            live are never re-checked. The artwork and
                            shipping checks are NOT affected; the artwork
                            checks have their own switch above.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-disable-store-gates-toggle"
                            v-model="disableStoreGates"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-disable-store-gates-toggle"
                            :disabled="storeGatesSaving"
                            @change="onStoreGatesToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ disableStoreGates ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-disable-store-gates-note"
                >
                    Off by default. While this is off, no store can go live
                    until an account Owner confirms the Master Services
                    Agreement (at the foot of the agreement page). Turn it on
                    only deliberately, and turn it back off when you are done:
                    while it is on, a store can go live for an account that
                    cannot take payments.
                </p>

                <p
                    v-if="storeGatesErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-disable-store-gates-error"
                >
                    {{ storeGatesErrorMsg }}
                </p>
            </div>

            <div
                class="sa-settings__card"
                data-testid="sa-setting-net-obligation-sweep"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-net-obligation-sweep-toggle"
                        >
                            Daily ACH obligation sweep
                        </label>
                        <p class="sa-settings__setting-help">
                            Collects nothing today. Decoration, platform
                            fees and Net Checkout shipping are billed on the
                            distributor invoice on the 1st and the 15th, for
                            Net Checkout and payroll deduction orders alike.
                            If a charge is ever moved back onto this job, it
                            runs daily and debits each distributor's bank
                            account for it. The debits are real and nobody
                            reviews them first.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-net-obligation-sweep-toggle"
                            v-model="netObligationSweepEnabled"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-net-obligation-sweep-toggle"
                            :disabled="netObligationSweepSaving"
                            @change="onNetObligationSweepToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ netObligationSweepEnabled ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-net-obligation-sweep-note"
                >
                    Off by default. Turning this on starts moving real money
                    out of real bank accounts, and a debit that has already
                    been sent cannot be called back the way a software change
                    can be undone. Leave it off until the collection rail has
                    been checked end to end.
                </p>

                <p
                    v-if="netObligationSweepErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-net-obligation-sweep-error"
                >
                    {{ netObligationSweepErrorMsg }}
                </p>
            </div>

            <!--
              Billing lapse enforcement (decision D6, Jake 2026-10-30).
              OFF by default (unseeded). The backend reader fails closed.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-billing-lapse"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-billing-lapse-toggle"
                        >
                            Lapse distributors for unpaid store invoices
                        </label>
                        <p class="sa-settings__setting-help">
                            When on, a distributor whose monthly store invoice
                            is still unpaid 14 days after it was issued (and
                            Stripe confirms nothing is paid or in progress)
                            becomes read-only: their storefronts stay open to
                            browse but take no new orders. An uncollectible
                            invoice still counts as unpaid. They are restored
                            automatically once a payment method is on file
                            and nothing is left owed or held for review.
                            Distributors who reached the end of the no-card
                            grace period without a payment method are also
                            held to read-only.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-billing-lapse-toggle"
                            v-model="billingLapseEnabled"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-billing-lapse-toggle"
                            :disabled="billingLapseSaving"
                            @change="onBillingLapseToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ billingLapseEnabled ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-billing-lapse-note"
                >
                    Off by default. It has no effect unless store-month
                    invoicing is live on this deployment. Shoppers on a
                    lapsed store see "This store is not taking orders right
                    now. Please contact the store."
                </p>

                <p
                    v-if="billingLapseErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-billing-lapse-error"
                >
                    {{ billingLapseErrorMsg }}
                </p>
            </div>

            <!--
              Stripe platforms (dual-mode unit 5) -- READ-ONLY.

              Shows which Stripe platforms THIS deployment holds a key for
              and how many distributors sit on each.

              IT DISPLAYS; IT DOES NOT SWITCH, and that is the design rather
              than a scoping accident. Mode is PER DISTRIBUTOR, precisely so
              real distributors can run live while others are still tested in
              sandbox on the same deployment -- a global toggle here would
              contradict the whole feature. The one place a mode changes is
              the control on the distributor detail page. Do not "finish"
              this panel by adding a switch.

              WHY BOTH NUMBERS. Either alone is inert: an unset
              STRIPE_LIVE_SECRET_KEY is harmless with zero live distributors
              and is a production outage with one. That pairing is exactly
              what the API's live-platform preflight refuses to boot on, so
              showing them together is what lets an admin see it coming
              instead of meeting it at a charge. The warning fires ONLY on
              unconfigured-AND-populated: warning on an empty unconfigured
              platform would train admins to ignore the one that matters.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-stripe-platforms"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <span class="sa-settings__setting-label">
                            Stripe platforms
                        </span>
                        <p
                            class="sa-settings__setting-note"
                            data-testid="sa-setting-stripe-platforms-note"
                        >
                            Which Stripe platforms this deployment is
                            configured for, and how many distributors are on
                            each. Read-only: a distributor's platform is set
                            on its own detail page, not here.
                        </p>
                    </div>
                </div>

                <p
                    v-if="stripePlatformsErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-stripe-platforms-error"
                >
                    {{ stripePlatformsErrorMsg }}
                </p>

                <ul v-else class="sa-settings__platform-list">
                    <li
                        v-for="p in stripePlatforms"
                        :key="p.mode"
                        class="sa-settings__platform-row"
                        :data-testid="`sa-setting-stripe-platform-${p.mode}`"
                    >
                        <span class="sa-settings__platform-name">
                            {{ platformLabel(p.mode) }}
                        </span>
                        <span class="sa-settings__platform-state">
                            {{
                                p.configured
                                    ? "Key configured"
                                    : "Not configured -- no API key on this deployment"
                            }}
                        </span>
                        <span class="sa-settings__platform-count">
                            {{ p.distributor_count }}
                            {{
                                p.distributor_count === 1
                                    ? "distributor"
                                    : "distributors"
                            }}
                        </span>
                        <span
                            v-if="!p.configured && p.distributor_count > 0"
                            class="sa-settings__error"
                            :data-testid="`sa-setting-stripe-platform-${p.mode}-warning`"
                        >
                            Charges for these distributors will fail until a
                            key for this platform is set.
                        </span>
                    </li>
                </ul>
            </div>

            <div class="sa-settings__card" data-testid="sa-setting-enable-inline-images">
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-enable-inline-images-switch"
                        >
                            Inline images in rich text
                        </label>
                        <p class="sa-settings__setting-help">
                            When on, store editors can insert images into
                            welcome messages and category text. Uploaded
                            images are stored in our asset store. Off by
                            default.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-enable-inline-images-switch"
                            v-model="enableInlineImages"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-enable-inline-images-switch"
                            :disabled="inlineImagesSaving"
                            @change="onInlineImagesToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ enableInlineImages ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-enable-inline-images-note"
                >
                    Off by default. Turn on to let store editors add images
                    to welcome messages and category text.
                </p>

                <p
                    v-if="inlineImagesErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-enable-inline-images-error"
                >
                    {{ inlineImagesErrorMsg }}
                </p>
            </div>

            <div class="sa-settings__card" data-testid="sa-setting-enable-departments">
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            for="sa-setting-enable-departments-switch"
                        >
                            Departments
                        </label>
                        <p class="sa-settings__setting-help">
                            When on, departments appear across the product:
                            the client Budget page, the departments panel on
                            a client, the store department mapping, and the
                            department picker at checkout. On by default.
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            id="sa-setting-enable-departments-switch"
                            v-model="enableDepartments"
                            type="checkbox"
                            class="sa-toggle__input"
                            data-testid="sa-setting-enable-departments-switch"
                            :disabled="departmentsSaving"
                            @change="onDepartmentsToggle"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span class="sa-toggle__state">
                            {{ enableDepartments ? "On" : "Off" }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-enable-departments-note"
                >
                    Turning this off hides the department surfaces only.
                    Existing department data is kept, and orders already
                    attributed to a department stay attributed.
                </p>

                <p
                    v-if="departmentsErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-enable-departments-error"
                >
                    {{ departmentsErrorMsg }}
                </p>
            </div>

            <PreviewFallbackToolsCard />

            <!--
              DTF art cleanup (Plan A, Jake 2026-10-29): the switch (default
              OFF) and the fees / vendor cost behind it.
            -->
            <DtfArtCleanupCard />

            <!--
              Support email (card #573, Jake 2026-10-29): the address customers
              are told to write to for help (the DTF low-resolution note when
              art cleanup is off).
            -->
            <SupportEmailCard />

            <FundraisingFlagCard />

            <!-- Card #645: distributor Orders page Add New Order, default OFF. -->
            <AddNewOrderCard />

            <div
                class="sa-settings__card"
                data-testid="sa-setting-storefront-url"
            >
                <div class="sa-settings__card-copy">
                    <span class="sa-settings__setting-label">
                        Storefront link address
                    </span>
                    <p class="sa-settings__setting-help">
                        The address customers see in emails we send them.
                        By default stores live on this app's own host; point
                        them at a dedicated domain here once its DNS is live.
                        A store with its own link domain (set on that store's
                        Branding page) uses that instead of this.
                    </p>
                </div>

                <!--
                  Unrecognised template: a value set through the API that
                  matches neither composable shape (a whitelabel URL, say).
                  Shown verbatim, with NO mode preselected, because
                  selecting a mode re-composes the template and would
                  silently destroy it. The radios below stay live and
                  unchecked so an SA can still deliberately move to a
                  managed shape -- an explicit click is consent to
                  replace, whereas preselecting one would make the next
                  unrelated save overwrite this value by accident.
                -->
                <template v-if="storefrontUnrecognised">
                    <p
                        class="sa-settings__storefront-raw"
                        data-testid="sa-setting-storefront-url-raw"
                    >
                        {{ storefrontTemplate }}
                    </p>
                    <p
                        class="sa-settings__not-enforced"
                        data-testid="sa-setting-storefront-url-custom-note"
                    >
                        This address was set outside this UI, through the API,
                        and does not match either option below. It is left as
                        it is -- pick an option below only if you mean to
                        replace it.
                    </p>
                </template>

                <ul class="sa-settings__mode-list">
                    <li
                        v-for="opt in storefrontModes"
                        :key="opt.value"
                        class="sa-settings__mode-row"
                    >
                        <label
                            class="sa-settings__mode-label"
                            :for="`sa-setting-storefront-url-mode-${opt.value}`"
                        >
                            <input
                                :id="`sa-setting-storefront-url-mode-${opt.value}`"
                                type="radio"
                                name="sa-setting-storefront-url-mode"
                                class="sa-settings__mode-radio"
                                :data-testid="`sa-setting-storefront-url-mode-${opt.value}`"
                                :value="opt.value"
                                :checked="storefrontMode === opt.value"
                                :disabled="storefrontSaving"
                                @change="onStorefrontModeChange(opt.value)"
                            />
                            {{ opt.label }}
                        </label>
                    </li>
                </ul>

                <div
                    v-if="storefrontMode !== 'default' && storefrontMode !== ''"
                    class="sa-settings__domain-field"
                >
                    <label
                        class="sa-settings__domain-label"
                        for="sa-setting-storefront-url-domain-input"
                    >
                        Domain
                    </label>
                    <input
                        id="sa-setting-storefront-url-domain-input"
                        v-model="storefrontDomain"
                        type="text"
                        class="sa-settings__domain-input"
                        data-testid="sa-setting-storefront-url-domain-input"
                        placeholder="shop.threadflow.com"
                        autocomplete="off"
                        spellcheck="false"
                        :disabled="storefrontSaving"
                        @change="onStorefrontDomainChange"
                    />
                </div>

                <p
                    class="sa-settings__storefront-preview"
                    data-testid="sa-setting-storefront-url-preview"
                >
                    A store with the slug
                    <code>{{ SAMPLE_SLUG }}</code> is reached at
                    <code class="sa-settings__storefront-preview-url">{{
                        storefrontPreview
                    }}</code>
                </p>

                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-storefront-url-note"
                >
                    Affects new emails only. Links in messages we have already
                    sent keep pointing at the old address, so keep the previous
                    one serving until those have run their course.
                </p>

                <p
                    v-if="storefrontErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-storefront-url-error"
                >
                    {{ storefrontErrorMsg }}
                </p>
            </div>

            <div
                class="sa-settings__card"
                data-testid="sa-setting-address-validation-provider"
            >
                <div class="sa-settings__card-copy">
                    <span class="sa-settings__setting-label">
                        Address validation provider
                    </span>
                    <p class="sa-settings__setting-help">
                        Which service checks that a shipping address is real.
                        This applies everywhere an address is entered --
                        storefront checkout, campaign locations, order
                        editing, distributor and vendor setup, and sign-up.
                    </p>
                </div>

                <!--
                  A RADIO PAIR, not an On/Off toggle. With a toggle an SA has
                  to guess which provider "On" means, and the label would
                  have to be rewritten the day a third provider appears. Both
                  options are named.
                -->
                <ul class="sa-settings__mode-list">
                    <li
                        v-for="opt in addressProviders"
                        :key="opt.value"
                        class="sa-settings__mode-row"
                    >
                        <label
                            class="sa-settings__mode-label"
                            :for="`sa-setting-address-validation-provider-${opt.value}`"
                        >
                            <input
                                :id="`sa-setting-address-validation-provider-${opt.value}`"
                                type="radio"
                                name="sa-setting-address-validation-provider"
                                class="sa-settings__mode-radio"
                                :data-testid="`sa-setting-address-validation-provider-${opt.value}`"
                                :value="opt.value"
                                :checked="addressProvider === opt.value"
                                :disabled="addressProviderSaving"
                                @change="onAddressProviderChange(opt.value)"
                            />
                            {{ opt.label }}
                        </label>
                    </li>
                </ul>

                <!--
                  The deconetwork option CANNOT validate addresses yet. The
                  back office exposes the route but has no carrier
                  credentials behind it, so every request 422s and our seam
                  returns the address unvalidated. Offering the option
                  without saying so is exactly the looks-wired-but-inert
                  failure this setting is meant to avoid, so the warning is
                  always visible rather than appearing only after selection.
                -->
                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-address-validation-provider-stub-note"
                >
                    The ThreadFlow Decorator app validates addresses through
                    the back office, which cannot do it yet -- its USPS
                    account is not licensed for address validation. Until it
                    is, picking it means addresses are accepted exactly as
                    typed (or checked by ShipEngine, if the ShipEngine
                    fallback switch below is On). Leave this on ShipEngine
                    until the license is in place.
                </p>

                <p
                    v-if="addressProviderErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-address-validation-provider-error"
                >
                    {{ addressProviderErrorMsg }}
                </p>
            </div>

            <div
                class="sa-settings__card"
                data-testid="sa-setting-shipping-rate-provider"
            >
                <div class="sa-settings__card-copy">
                    <span class="sa-settings__setting-label">
                        Shipping rate provider
                    </span>
                    <p class="sa-settings__setting-help">
                        Which service quotes live shipping costs at
                        checkout. The ThreadFlow Decorator app is tried
                        first; if it cannot quote a rate, ShipEngine is
                        used only when its switch below is On.
                    </p>
                    <!-- Live-rate shipping card (Jake, 2026-10-21): the
                         origin is resolved server-side, services/ship_from. -->
                    <p
                        class="sa-settings__setting-help"
                        data-testid="sa-setting-shipping-rate-provider-origin"
                    >
                        Every store that charges shipping without a flat
                        rate gets a live rate. It ships from the client
                        company's shipping address, else the distributor's
                        ship-from address on Company, else the store's
                        decorator. A store with none of these cannot launch
                        and cannot take orders.
                    </p>
                </div>

                <ul class="sa-settings__mode-list">
                    <li
                        v-for="opt in shippingRateProviders"
                        :key="opt.value"
                        class="sa-settings__mode-row"
                    >
                        <label
                            class="sa-settings__mode-label"
                            :for="`sa-setting-shipping-rate-provider-${opt.value}`"
                        >
                            <input
                                :id="`sa-setting-shipping-rate-provider-${opt.value}`"
                                type="radio"
                                name="sa-setting-shipping-rate-provider"
                                class="sa-settings__mode-radio"
                                :data-testid="`sa-setting-shipping-rate-provider-${opt.value}`"
                                :value="opt.value"
                                :checked="shippingRateProvider === opt.value"
                                :disabled="shippingRateProviderSaving"
                                @change="onShippingRateProviderChange(opt.value)"
                            />
                            {{ opt.label }}
                        </label>
                    </li>
                </ul>

                <!--
                  Unlike the address-validation card above, picking the back
                  office here is SAFE: where it cannot quote (an environment
                  with no carrier accounts, e.g. stage), the checkout path
                  falls back to ShipEngine on its own. The note says what
                  actually happens rather than warning the SA off the option.
                -->
                <p
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-shipping-rate-provider-note"
                >
                    Where the Decorator app cannot quote a rate (for
                    example, an environment whose shipping carrier accounts
                    are not connected), the rate comes from ShipEngine
                    instead -- but only if the "Use ShipEngine for shipping
                    rates" switch below is On. Leave this on the Decorator
                    app -- it is used wherever it can quote, and ShipEngine
                    is a trial with a limited number of calls per month.
                    Pick ShipEngine only to stop using the Decorator app
                    entirely.
                </p>

                <p
                    v-if="shippingRateProviderErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-shipping-rate-provider-error"
                >
                    {{ shippingRateProviderErrorMsg }}
                </p>
            </div>

            <!--
              ShipEngine fallback switches (Jake, 2026-10-22). Two independent
              on/off cards: rates ON by default (Jake, 2026-10-27), address
              validation OFF by default. They are master switches for
              ShipEngine (ShipStation): OFF means it is never called. The
              copy says what the shopper sees, because OFF is a real product
              behavior on an environment (stage) whose back office has no
              carrier accounts, not a harmless default.
            -->
            <div
                v-for="sw in shipengineSwitches"
                :key="sw.id"
                class="sa-settings__card"
                :data-testid="`sa-setting-${sw.id}`"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <label
                            class="sa-settings__setting-label"
                            :for="`sa-setting-${sw.id}-toggle`"
                        >
                            {{ sw.title }}
                        </label>
                        <p class="sa-settings__setting-help">
                            {{ sw.help }}
                        </p>
                    </div>

                    <label class="sa-toggle">
                        <input
                            :id="`sa-setting-${sw.id}-toggle`"
                            v-model="shipengineState[sw.id].enabled"
                            type="checkbox"
                            class="sa-toggle__input"
                            :data-testid="`sa-setting-${sw.id}-toggle`"
                            :disabled="
                                shipengineState[sw.id].saving ||
                                shipengineState[sw.id].enabled === null
                            "
                            @change="onShipengineSwitchToggle(sw)"
                        />
                        <span class="sa-toggle__track" aria-hidden="true">
                            <span class="sa-toggle__thumb"></span>
                        </span>
                        <span
                            class="sa-toggle__state"
                            :data-testid="`sa-setting-${sw.id}-state`"
                        >
                            {{ shipengineStateLabel(shipengineState[sw.id].enabled) }}
                        </span>
                    </label>
                </div>

                <p
                    class="sa-settings__not-enforced"
                    :data-testid="`sa-setting-${sw.id}-note`"
                >
                    {{ sw.note }}
                </p>

                <p
                    v-if="shipengineState[sw.id].error"
                    class="sa-settings__error"
                    :data-testid="`sa-setting-${sw.id}-error`"
                >
                    {{ shipengineState[sw.id].error }}
                </p>
            </div>

            <!--
              Tech contact email (GET/PATCH
              /api/platform-settings/tech-contact-email; Jake, 2026-10-27).
              The one internal address that hears about live shipping-rate
              failures. The server owns the value in force, the default and
              the validation (one plain address); this card only shows the
              server's answer and its refusal sentence. Reset sends null.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-tech-contact"
            >
                <div class="sa-settings__card-copy">
                    <label
                        class="sa-settings__setting-label"
                        for="sa-setting-tech-contact-input"
                    >
                        Tech contact email
                    </label>
                    <p
                        class="sa-settings__setting-help"
                        data-testid="sa-setting-tech-contact-note"
                    >
                        Receives: Shipping-rate failure alerts -- a store on
                        live shipping rates could not get a rate in the cart,
                        or an order was refused at checkout for its shipping
                        quote. At most one email per store and reason each
                        hour. One plain address, such as ops@example.com.
                        Outside production, alerts go out only once an
                        address is saved here.
                    </p>
                </div>

                <template v-if="techContactLoaded">
                    <p class="sa-settings__not-enforced">
                        In force:
                        <code data-testid="sa-setting-tech-contact-value">{{
                            techContact.value
                        }}</code>
                        <span
                            v-if="techContact.is_default"
                            data-testid="sa-setting-tech-contact-default"
                        >
                            (default)
                        </span>
                    </p>
                    <div class="sa-settings__domain-field">
                        <input
                            id="sa-setting-tech-contact-input"
                            v-model="techContactInput"
                            type="email"
                            autocomplete="off"
                            class="sa-settings__domain-input"
                            data-testid="sa-setting-tech-contact-input"
                            :disabled="techContactSaving"
                        />
                    </div>
                    <div class="sa-settings__po-prefix-row">
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-tech-contact-save"
                            :disabled="techContactSaving"
                            @click="onTechContactSave"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-tech-contact-reset"
                            :disabled="techContactSaving"
                            @click="onTechContactReset"
                        >
                            Reset to default
                        </button>
                    </div>
                </template>

                <p
                    v-if="techContactSaved"
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-tech-contact-saved"
                >
                    Saved. New alerts go to this address.
                </p>

                <p
                    v-if="techContactErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-tech-contact-error"
                >
                    {{ techContactErrorMsg }}
                </p>
            </div>

            <!--
              WHICH of Dustin's two back offices receives every order on the
              platform.

              SHOWING THE MODE HERE IS CORRECT, INCLUDING WHEN IT IS PROD.
              Jake's "no prod indicator" constraint (2026-09-24) is about
              surfaces a CUSTOMER can see, and this page is not one -- an
              admin flipping a switch must be able to see what it is set to.
              The customer-facing half of that constraint is the status bar
              in App.vue, which shows a chip on STAGE and nothing on PROD.

              THE PROD OPTION IS DISABLED WHERE THE SERVER WOULD REFUSE IT,
              with the server's own reason shown. The backend 409s prod on a
              sandbox and on any environment with no prod credential; an
              option that is offered and then fails on click reads as a bug
              rather than a policy, and the next person routes around the
              guard instead of respecting it.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-deconetwork-mode"
            >
                <div class="sa-settings__card-copy">
                    <span class="sa-settings__setting-label">
                        Decorator back office
                    </span>
                    <p class="sa-settings__setting-help">
                        Which ThreadFlow Decorator back office receives
                        orders, batches and decorator lookups. Production
                        puts real work orders on a real decorator's queue
                        and real garments get made -- use staging for
                        anything that is not a real customer order.
                    </p>
                </div>

                <ul class="sa-settings__mode-list">
                    <li
                        v-for="opt in backOfficeModes"
                        :key="opt.value"
                        class="sa-settings__mode-row"
                    >
                        <label
                            class="sa-settings__mode-label"
                            :for="`sa-setting-deconetwork-mode-${opt.value}`"
                        >
                            <input
                                :id="`sa-setting-deconetwork-mode-${opt.value}`"
                                type="radio"
                                name="sa-setting-deconetwork-mode"
                                class="sa-settings__mode-radio"
                                :data-testid="`sa-setting-deconetwork-mode-${opt.value}`"
                                :value="opt.value"
                                :checked="backOfficeMode === opt.value"
                                :disabled="
                                    backOfficeModeSaving ||
                                    (opt.value === BACK_OFFICE_PROD &&
                                        !backOfficeProdSelectable)
                                "
                                @change="onBackOfficeModeChange(opt.value)"
                            />
                            {{ opt.label }}
                        </label>
                    </li>
                </ul>

                <!--
                  The server's own sentence, not a paraphrase: it names
                  WHICH of the two refusals applies (sandbox, or missing
                  prod credential), and those have different fixes.
                -->
                <p
                    v-if="backOfficeProdBlockedReason"
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-deconetwork-mode-blocked-note"
                >
                    {{ backOfficeProdBlockedReason }}
                </p>

                <p
                    v-if="backOfficeModeErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-deconetwork-mode-error"
                >
                    {{ backOfficeModeErrorMsg }}
                </p>
            </div>

            <!--
              The purchase_order PREFIX every order is sent to the decorator
              back office under (Jake, 2026-10-01, Option B): TF production,
              and off production whatever is stored here -- pre-seeded at boot
              (TFS-<port> sandbox, TFS stage, TFB beta, TFD dev) and changeable by an SA
              to any valid non-production prefix, never bare TF.

              The card leads with the prefix IN FORCE and where it came from
              (seeded / set on this page / pinned). Save sends what is in the
              box; a refusal is the server's own sentence, because it says
              what is wrong with the value.

              READ-ONLY ON PRODUCTION: TF is pinned there and the server
              refuses anything else, so no control is offered.

              A PROBLEM IS SHOWN, NOT HIDDEN: with no environment identity, or
              a missing or invalid value (TF restored from a production
              snapshot), every order is being refused until it is fixed -- and
              saving a valid prefix here is the fix, so the control stays.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-po-prefix"
            >
                <div class="sa-settings__card-copy">
                    <span class="sa-settings__setting-label">
                        Decorator order number prefix
                    </span>
                    <p class="sa-settings__setting-help">
                        Every order sent to the ThreadFlow Decorator back
                        office is numbered with this prefix, so orders from
                        different environments never collide there. It is set
                        automatically for this environment and can be changed
                        here; production's is fixed at TF.
                    </p>
                </div>

                <template v-if="poPrefixLoaded">
                    <p class="sa-settings__po-prefix-current">
                        In force:
                        <!--
                          The value-keyed wrapper lets automation wait for a
                          SPECIFIC prefix to be in force after a save,
                          rather than racing the request.
                        -->
                        <span
                            :data-testid="`sa-setting-po-prefix-in-force-${poPrefix.value || 'none'}`"
                        >
                            <code data-testid="sa-setting-po-prefix-value">{{
                                poPrefix.value || "none"
                            }}</code>
                        </span>
                        <span
                            class="sa-settings__po-prefix-source"
                            data-testid="sa-setting-po-prefix-source"
                        >
                            {{ poPrefixSourceText }}
                        </span>
                    </p>
                    <p
                        v-if="poPrefix.value"
                        class="sa-settings__storefront-preview"
                        data-testid="sa-setting-po-prefix-example"
                    >
                        Order 1234 is sent as
                        <code>{{ poPrefix.value }}-1234</code>
                    </p>

                    <p
                        v-if="poPrefix.problem"
                        class="sa-settings__error"
                        data-testid="sa-setting-po-prefix-problem"
                    >
                        Orders are not being sent: {{ poPrefix.problem }}
                    </p>

                    <p
                        v-if="poPrefix.environment === 'prod'"
                        class="sa-settings__not-enforced"
                        data-testid="sa-setting-po-prefix-readonly-note"
                    >
                        Production's prefix is fixed at TF and cannot be
                        changed.
                    </p>
                    <div
                        v-else-if="poPrefix.editable"
                        class="sa-settings__domain-field"
                    >
                        <label
                            class="sa-settings__domain-label"
                            for="sa-setting-po-prefix-input"
                        >
                            Prefix
                        </label>
                        <div class="sa-settings__po-prefix-row">
                            <input
                                id="sa-setting-po-prefix-input"
                                v-model="poPrefixInput"
                                type="text"
                                class="sa-settings__domain-input"
                                data-testid="sa-setting-po-prefix-input"
                                autocomplete="off"
                                spellcheck="false"
                                :disabled="poPrefixSaving"
                            />
                            <button
                                type="button"
                                class="sa-settings__button"
                                data-testid="sa-setting-po-prefix-save"
                                :disabled="poPrefixSaving"
                                @click="onPoPrefixSave(poPrefixInput)"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </template>

                <p
                    v-if="poPrefixSaved"
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-po-prefix-saved"
                >
                    Saved. The next order sent uses this prefix.
                </p>

                <p
                    v-if="poPrefixErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-po-prefix-error"
                >
                    {{ poPrefixErrorMsg }}
                </p>
            </div>

            <!--
              Risk Watch thresholds. Four numbers that decide when a
              distributor lands in Risk Watch (GET/PATCH
              /api/platform-settings/risk-watch-thresholds). The server owns
              the value in force, the default and the allowed range; this card
              renders them and computes none. Save sends all four; Reset sends
              null for all four, which puts each back to its default.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-risk-watch"
            >
                <div class="sa-settings__card-copy">
                    <span class="sa-settings__setting-label">
                        Risk Watch thresholds
                    </span>
                    <p class="sa-settings__setting-help">
                        These decide when a distributor is flagged in Risk
                        Watch. A change applies at the next Risk Watch check;
                        flags already raised are not removed.
                    </p>
                </div>

                <template v-if="riskWatchLoaded">
                    <div
                        v-for="f in RISK_WATCH_FIELDS"
                        :key="f.field"
                        class="sa-settings__domain-field"
                    >
                        <label
                            class="sa-settings__domain-label"
                            :for="`sa-setting-risk-watch-input-${f.field}`"
                        >
                            {{ f.label }} ({{ f.unit }})
                        </label>
                        <p class="sa-settings__setting-help">{{ f.help }}</p>
                        <!--
                          The value-keyed wrapper lets automation wait for a
                          SPECIFIC value to be in force after a save, rather
                          than racing the request.
                        -->
                        <span
                            :data-testid="`sa-setting-risk-watch-in-force-${f.field}-${riskWatch[f.field].value}`"
                        >
                            <input
                                :id="`sa-setting-risk-watch-input-${f.field}`"
                                v-model="riskWatchInputs[f.field]"
                                type="number"
                                step="1"
                                class="sa-settings__domain-input"
                                :data-testid="`sa-setting-risk-watch-input-${f.field}`"
                                :disabled="riskWatchSaving"
                            />
                        </span>
                        <p
                            class="sa-settings__not-enforced"
                            :data-testid="`sa-setting-risk-watch-range-${f.field}`"
                        >
                            Allowed {{ riskWatch[f.field].min }} to
                            {{ riskWatch[f.field].max }}; default
                            {{ riskWatch[f.field].default }}.
                        </p>
                    </div>
                    <div class="sa-settings__po-prefix-row">
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-risk-watch-save"
                            :disabled="riskWatchSaving"
                            @click="onRiskWatchSave"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-risk-watch-reset"
                            :disabled="riskWatchSaving"
                            @click="onRiskWatchReset"
                        >
                            Reset to defaults
                        </button>
                    </div>
                </template>

                <p
                    v-if="riskWatchSaved"
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-risk-watch-saved"
                >
                    Saved. The next Risk Watch check uses these numbers.
                </p>

                <p
                    v-if="riskWatchErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-risk-watch-error"
                >
                    {{ riskWatchErrorMsg }}
                </p>
            </div>

            <!--
              Embroidery size estimate: stitches per square inch (GET/PATCH
              /api/platform-settings/estimated-stitches-per-sq-in). Prices
              embroidery that has no recorded stitch count (no stitch file,
              digitized file or estimate). The server owns the value in force,
              the default and the allowed range; this card computes none of
              them. Reset sends null, which puts the default back.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-stitch-density"
            >
                <div class="sa-settings__card-copy">
                    <label
                        class="sa-settings__setting-label"
                        for="sa-setting-stitch-density-input"
                    >
                        Estimated stitches per square inch
                    </label>
                    <p class="sa-settings__setting-help">
                        Embroidery with no stitch count yet is priced from its
                        size: width times height times this number.
                    </p>
                    <p
                        class="sa-settings__setting-help"
                        data-testid="sa-setting-stitch-density-scope"
                    >
                        Affects only estimates for decorations with no stitch
                        file, not orders already placed.
                    </p>
                </div>

                <template v-if="stitchDensityLoaded">
                    <div class="sa-settings__domain-field">
                        <!--
                          The value-keyed wrapper lets automation wait for a
                          SPECIFIC value to be in force after a save, rather
                          than racing the request.
                        -->
                        <span
                            :data-testid="`sa-setting-stitch-density-in-force-${stitchDensity.value}`"
                        >
                            <input
                                id="sa-setting-stitch-density-input"
                                v-model="stitchDensityInput"
                                type="number"
                                step="1"
                                class="sa-settings__domain-input"
                                data-testid="sa-setting-stitch-density-input"
                                :disabled="stitchDensitySaving"
                            />
                        </span>
                        <p
                            class="sa-settings__not-enforced"
                            data-testid="sa-setting-stitch-density-range"
                        >
                            Allowed {{ stitchDensity.min }} to
                            {{ stitchDensity.max }}; default
                            {{ stitchDensity.default }}.
                        </p>
                    </div>
                    <div class="sa-settings__po-prefix-row">
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-stitch-density-save"
                            :disabled="stitchDensitySaving"
                            @click="onStitchDensitySave"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-stitch-density-reset"
                            :disabled="stitchDensitySaving"
                            @click="onStitchDensityReset"
                        >
                            Reset to default
                        </button>
                    </div>
                </template>

                <p
                    v-if="stitchDensitySaved"
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-stitch-density-saved"
                >
                    Saved. New estimates use this number.
                </p>

                <p
                    v-if="stitchDensityErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-stitch-density-error"
                >
                    {{ stitchDensityErrorMsg }}
                </p>
            </div>

            <!--
              Product editor: color render delay (GET/PATCH
              /api/platform-settings/color-render-debounce-seconds). The
              server owns the value in force, the default and the allowed
              range; this card computes none of them. Reset sends null, which
              puts the default back.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-color-render-delay"
            >
                <div class="sa-settings__card-copy">
                    <label
                        class="sa-settings__setting-label"
                        for="sa-setting-color-render-delay-input"
                    >
                        Color render delay (seconds)
                    </label>
                    <p
                        class="sa-settings__setting-help"
                        data-testid="sa-setting-color-render-delay-help"
                    >
                        How long the editor waits after the last save before
                        rendering newly selected colors. A change applies the
                        next time a distributor opens the editor.
                    </p>
                </div>

                <template v-if="colorRenderDelayLoaded">
                    <div class="sa-settings__domain-field">
                        <input
                            id="sa-setting-color-render-delay-input"
                            v-model="colorRenderDelayInput"
                            type="number"
                            step="any"
                            class="sa-settings__domain-input"
                            data-testid="sa-setting-color-render-delay-input"
                            :disabled="colorRenderDelaySaving"
                        />
                        <!--
                          Static testid; the number in force is its text (and
                          data-value), so automation can wait for a SPECIFIC
                          value after a save rather than racing the request.
                        -->
                        <p
                            class="sa-settings__not-enforced"
                            data-testid="sa-setting-color-render-delay-in-force"
                            :data-value="colorRenderDelay.value"
                        >
                            In force: {{ colorRenderDelay.value }} seconds
                        </p>
                        <p
                            class="sa-settings__not-enforced"
                            data-testid="sa-setting-color-render-delay-range"
                        >
                            Allowed {{ colorRenderDelay.min }} to
                            {{ colorRenderDelay.max }}; default
                            {{ colorRenderDelay.default }}.
                        </p>
                    </div>
                    <div class="sa-settings__po-prefix-row">
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-color-render-delay-save"
                            :disabled="colorRenderDelaySaving"
                            @click="onColorRenderDelaySave"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            class="sa-settings__button"
                            data-testid="sa-setting-color-render-delay-reset"
                            :disabled="colorRenderDelaySaving"
                            @click="onColorRenderDelayReset"
                        >
                            Reset to default
                        </button>
                    </div>
                </template>

                <p
                    v-if="colorRenderDelaySaved"
                    class="sa-settings__not-enforced"
                    data-testid="sa-setting-color-render-delay-saved"
                >
                    Saved. The editor uses this delay from its next load.
                </p>

                <p
                    v-if="colorRenderDelayErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-color-render-delay-error"
                >
                    {{ colorRenderDelayErrorMsg }}
                </p>
            </div>

            <div
                class="sa-settings__card"
                data-testid="sa-setting-decoration-methods"
            >
                <div class="sa-settings__card-copy">
                    <span class="sa-settings__setting-label">
                        Allowed decoration methods
                    </span>
                    <p class="sa-settings__setting-help">
                        Only the checked methods are selectable when a
                        Builder adds a decoration. Unchecked methods stay in
                        the catalog (existing decorations using them keep
                        working) but are hidden from every method picker.
                    </p>
                </div>

                <ul class="sa-settings__method-list">
                    <li
                        v-for="method in knownMethods"
                        :key="method.slug"
                        class="sa-settings__method-row"
                    >
                        <label
                            class="sa-settings__method-label"
                            :for="`sa-setting-decoration-method-${method.slug}`"
                        >
                            <input
                                :id="`sa-setting-decoration-method-${method.slug}`"
                                type="checkbox"
                                class="sa-settings__method-checkbox"
                                :data-testid="`sa-setting-decoration-method-${method.slug}`"
                                :checked="allowedMethodSlugs.includes(method.slug)"
                                :disabled="methodsSaving"
                                @change="onMethodToggle(method.slug, $event)"
                            />
                            {{ method.display_name || method.slug }}
                        </label>
                    </li>
                </ul>

                <p
                    v-if="methodsErrorMsg"
                    class="sa-settings__error"
                    data-testid="sa-setting-decoration-methods-error"
                >
                    {{ methodsErrorMsg }}
                </p>
            </div>

            <!--
              THERE IS DELIBERATELY NO API TOKENS CARD HERE.

              #3810 shipped the mint/reveal/revoke UI as a card on this page;
              #3811 replaced it with a link card to the new /admin/api-tokens
              page. Both were reasonable while the SA sidebar had no entry for
              that page -- without a card it was reachable only by typing the
              URL.

              Jake's ask was the SIDEBAR, though: "a link directly under the
              Settings link in the left nav, labeled 'API Tokens'". That entry
              exists now (SuperAdminLayout.vue, sa-nav-api-tokens, between
              Settings and Operations), so a card here would be a second
              mechanism for one destination -- one concept, one mechanism
              (docs/design-philosophy.md).

              ITS ABSENCE IS TESTED, not merely intended:
              __tests__/internal-api-tokens.spec.js asserts the card AND its
              link are gone, with the Placement Zone Dimensions card asserted
              PRESENT as the control -- otherwise "the card is absent" would
              also pass on a page that rendered nothing at all.
            -->

            <!--
              Placement Zone Dimensions -- a discoverable entry point to the
              SA editor at /admin/placement-zones/dimensions (route name
              sa_placement_zone_dimensions). This card is a NAV link, not a
              setting mutated in place: the editor lived at a direct-URL-only
              route with no navigation into it, so an SA could not find it.
              Store Decorations redesign https://trello.com/c/oSOHJfg0.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-placement-zone-dimensions"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <span class="sa-settings__setting-label">
                            Placement Zone Dimensions
                        </span>
                        <p class="sa-settings__setting-help">
                            Edit the per-zone printable areas that drive
                            decoration fit verdicts (provisional per ADR 0065).
                        </p>
                    </div>

                    <router-link
                        ref="zoneDimsLink"
                        :to="{ name: 'sa_placement_zone_dimensions' }"
                        class="sa-settings__nav-link"
                        data-testid="sa-setting-placement-zone-dimensions-link"
                    >
                        Open editor
                    </router-link>
                </div>
            </div>

            <!--
              Artwork resolution -- a NAV link to the per-method DPI levels at
              /admin/decoration-processes/dpi-thresholds (route name
              sa_dpi_thresholds): the recommended DPI, the minimum DPI and the
              Reject / Warn action below it (card #573). Same shape as the
              Placement Zone Dimensions card above.
            -->
            <div
                class="sa-settings__card"
                data-testid="sa-setting-dpi-thresholds"
            >
                <div class="sa-settings__card-body">
                    <div class="sa-settings__card-copy">
                        <span class="sa-settings__setting-label">
                            Artwork resolution
                        </span>
                        <p class="sa-settings__setting-help">
                            Set each method's recommended and minimum DPI, and
                            whether artwork below the minimum is refused or
                            only flagged.
                        </p>
                    </div>

                    <router-link
                        ref="dpiThresholdsLink"
                        :to="{ name: 'sa_dpi_thresholds' }"
                        class="sa-settings__nav-link"
                        data-testid="sa-setting-dpi-thresholds-link"
                    >
                        Open editor
                    </router-link>
                </div>
            </div>
        </template>
    </section>
</template>

<script>
import { ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "@/axios";
import PreviewFallbackToolsCard from "./PreviewFallbackToolsCard.vue";
import DtfArtCleanupCard from "./DtfArtCleanupCard.vue";
import SupportEmailCard from "./SupportEmailCard.vue";
import FundraisingFlagCard from "./FundraisingFlagCard.vue";
import AddNewOrderCard from "./AddNewOrderCard.vue";

const URL = "/api/platform-settings/hide-unpreviewable-color-variants";
const SUPPRESS_URL = "/api/platform-settings/suppress-error-snackbars";
const INLINE_IMAGES_URL = "/api/platform-settings/enable-inline-images";
const DEPARTMENTS_URL = "/api/platform-settings/enable-departments";
const ARTWORK_GATES_URL =
    "/api/platform-settings/disable-artwork-launch-gates";
const STORE_GATES_URL =
    "/api/platform-settings/disable-store-launch-gates";
const NET_OBLIGATION_SWEEP_URL =
    "/api/platform-settings/net-obligation-sweep-enabled";
const BILLING_LAPSE_URL =
    "/api/platform-settings/billing-lapse-enforcement-enabled";
const METHODS_URL = "/api/platform-settings/allowed-decoration-method-slugs";
// Deliberately the UNFILTERED catalog endpoint (not gated by the
// allowlist itself -- this admin surface needs to render EVERY known
// method as a checkbox, including ones currently unchecked/disallowed,
// so an admin can widen the allowlist back to them).
const KNOWN_METHODS_URL = "/api/decoration_processes/";
const STOREFRONT_URL = "/api/platform-settings/store-base-url-template";
const ADDRESS_PROVIDER_URL =
    "/api/platform-settings/address-validation-provider";

// The two providers, named. `value` matches the backend allowlist exactly --
// the API rejects anything else, including a different case, rather than
// coercing, so these strings are a contract and not display text.
//
// "ShipEngine (ShipStation)" carries both names deliberately: the account is
// bought from ShipStation and the API is branded ShipEngine, and an SA who
// knows it by one name should not have to guess it is the same thing.
const ADDRESS_PROVIDERS = [
    { value: "shipengine", label: "ShipEngine (ShipStation) -- recommended" },
    { value: "deconetwork", label: "ThreadFlow Decorator app -- not ready" },
];

const ADDRESS_PROVIDER_DEFAULT = "shipengine";

const SHIPPING_RATE_PROVIDER_URL =
    "/api/platform-settings/shipping-rate-provider";

// The rate providers, named. `value` matches the backend allowlist exactly.
//
// NOTE the order and the labels are the REVERSE of ADDRESS_PROVIDERS above:
// for rates the back office is the recommended primary, because it is not
// metered and ShipEngine is a trial with a monthly call cap. The back office
// cannot rate yet either -- but unlike address validation, the caller falls
// back to ShipEngine automatically, so picking it is safe today.
const SHIPPING_RATE_PROVIDERS = [
    { value: "deconetwork", label: "ThreadFlow Decorator app -- recommended" },
    { value: "shipengine", label: "ShipEngine (ShipStation) -- fallback" },
];

const SHIPPING_RATE_PROVIDER_DEFAULT = "deconetwork";

// The two ShipEngine (ShipStation) fallback switches. The server resolves the
// defaults (rates ON since 2026-10-27, address validation OFF); `id` drives
// the data-testids (`sa-setting-<id>[-toggle|-state|-note|-error]`).
// Every ShipEngine call on the platform sits behind one of these, so OFF is a
// real product behavior, and the copy below says what a shopper sees.
const SHIPENGINE_SWITCHES = [
    {
        id: "shipengine-rate-fallback",
        url: "/api/platform-settings/shipengine-rate-fallback-enabled",
        title: "Use ShipEngine for shipping rates",
        help:
            "When the ThreadFlow Decorator app cannot quote a shipping rate, " +
            "fall back to ShipEngine (ShipStation) so the shopper still sees a " +
            "price. When this is off, ShipEngine is never called for rates.",
        note:
            "On by default. If you turn it off, then wherever the Decorator " +
            "app cannot quote a rate -- for example the staging back office, " +
            "which has no carrier accounts -- shipping reads as unavailable " +
            "(TBD) and checkout is blocked until a rate exists.",
        loadError: "Could not load the ShipEngine rate setting.",
    },
    {
        id: "shipengine-address-validation-fallback",
        url: "/api/platform-settings/shipengine-address-validation-fallback-enabled",
        title: "Use ShipEngine when the back office cannot validate addresses",
        help:
            "Address validation uses the back office. This switch only " +
            "controls whether ShipEngine (ShipStation) is used when the back " +
            "office cannot validate an address. It matters only when the " +
            "address validation provider above is the ThreadFlow Decorator " +
            "app; with ShipEngine selected, ShipEngine is the provider.",
        note:
            "Off by default. While this is off and the back office cannot " +
            "validate an address (for example its USPS account is not " +
            "licensed for address validation), the address is accepted as " +
            "typed and not marked validated: shoppers are not blocked, the " +
            "order is marked as having an unverified address, and saved " +
            "company addresses are not stamped as validated.",
        loadError: "Could not load the ShipEngine address validation fallback setting.",
    },
    {
        id: "address-freetext-parse",
        url: "/api/platform-settings/address-freetext-parse-enabled",
        title: "Let ShipEngine read pasted one-line addresses",
        help:
            "When someone pastes a whole address on one line, ShipEngine " +
            "(ShipStation) splits it into street, city, state and ZIP, and " +
            "the back office then validates those fields. ShipEngine only " +
            "reads the text; it never validates. Applies when the back " +
            "office validates addresses and the ShipEngine address " +
            "validation fallback above is off.",
        note:
            "On by default. While it is on, ShipEngine receives the full " +
            "pasted street text, and anything else pasted with it. Turn it " +
            "off to stop that: a pasted one-line address is then not " +
            "saved, and the user is asked to enter street, city, state " +
            "and ZIP.",
        loadError: "Could not load the free-text address parsing setting.",
    },
];

const PO_PREFIX_URL = "/api/platform-settings/deconetwork-po-prefix";
const RISK_WATCH_URL = "/api/platform-settings/risk-watch-thresholds";
const TECH_CONTACT_URL = "/api/platform-settings/tech-contact-email";
const STITCH_DENSITY_URL =
    "/api/platform-settings/estimated-stitches-per-sq-in";
const COLOR_RENDER_DELAY_URL =
    "/api/platform-settings/color-render-debounce-seconds";

// The four Risk Watch thresholds, in display order. `field` matches the API's
// field names exactly (a contract, not display text). Label, unit and help are
// the plain-language names; the value, default and allowed range come from the
// server.
const RISK_WATCH_FIELDS = [
    {
        field: "inactive_days",
        label: "Flag a distributor with no activity after",
        unit: "days",
        help: "Flagged when it has had no order and no account login for this many days.",
    },
    {
        field: "volume_window_days",
        label: "Order-volume comparison period",
        unit: "days",
        help: "Orders in this many recent days are compared with the same number of days before.",
    },
    {
        field: "volume_drop_pct",
        label: "Flag an order-volume drop of at least",
        unit: "percent",
        help: "Flagged when recent orders fell by at least this much; 100 means only a complete stop.",
    },
    {
        field: "volume_drop_min_baseline",
        label: "Minimum earlier orders before a drop counts",
        unit: "orders",
        help: "A drop is ignored unless the earlier period had at least this many orders.",
    },
];
const BACK_OFFICE_MODE_URL =
    "/api/platform-settings/deconetwork-back-office-mode";

// The two back offices. `value` matches the backend allowlist EXACTLY --
// the API compares without trimming or case-folding and rejects anything
// else, so these strings are a contract, not display text.
//
// STAGE FIRST, matching the backend's ordering and services/stripe_clients:
// the list reads in the safe-to-dangerous direction, so the harmless option
// is the one an eye lands on first.
//
// The prod label says what it DOES, not what it is called. "Production"
// alone is a word an admin can click past; "real orders to a real
// decorator" is the consequence, and this is the switch where the
// consequence is the whole point.
const BACK_OFFICE_STAGE = "stage";
const BACK_OFFICE_PROD = "prod";

const BACK_OFFICE_MODES = [
    { value: BACK_OFFICE_STAGE, label: "Staging -- safe for testing" },
    {
        value: BACK_OFFICE_PROD,
        label: "Production -- real orders to a real decorator",
    },
];

// Unrecognised values resolve here, matching the backend resolver. A value
// the UI does not know must never leave both radios unchecked (which would
// imply no back office is selected) and must never resolve toward prod.
const BACK_OFFICE_MODE_DEFAULT = BACK_OFFICE_STAGE;

// A real-looking slug for the live preview. The SA should see the shape a
// customer actually gets, not an abstract "{slug}" -- the whole point of
// composing the template for them is that they never meet the placeholder.
const SAMPLE_SLUG = "acme";

const STOREFRONT_MODES = [
    { value: "default", label: "Default -- this app's own address" },
    { value: "path", label: "Custom domain, slug in the path" },
    { value: "subdomain", label: "Custom domain, slug as a subdomain" },
];

/**
 * Split a stored template into {mode, domain}, or null if it matches
 * neither composable shape.
 *
 * The backend stores ONE field: a URL template carrying `{slug}` exactly
 * once. "Mode" is not persisted anywhere -- it is derived here and
 * re-composed on save, so there is a single write model and no second
 * setting to drift out of sync.
 *
 * Returning null for an unrecognised template is load-bearing. A
 * whitelabel value set through the API (say
 * `https://stores.example.com/x/{slug}/home`) parses into no mode; the
 * caller must then render it read-only. Coercing it into the closest
 * mode would silently rewrite a working customer-facing URL on the next
 * save.
 */
function parseStorefrontTemplate(template) {
    if (typeof template !== "string" || !template) {
        return { mode: "default", domain: "" };
    }
    // `https://<domain>/{slug}` -- slug in the path.
    const pathMatch = /^https:\/\/([^/{}\s]+)\/\{slug\}$/.exec(template);
    if (pathMatch) {
        return { mode: "path", domain: pathMatch[1] };
    }
    // `https://{slug}.<domain>` -- slug as a subdomain.
    const subMatch = /^https:\/\/\{slug\}\.([^/{}\s]+)$/.exec(template);
    if (subMatch) {
        return { mode: "subdomain", domain: subMatch[1] };
    }
    return null;
}

/**
 * Normalize what an SA typed into a bare domain.
 *
 * Pasting a full URL is the likely real-world input, so strip a leading
 * scheme and any trailing slashes rather than composing
 * `https://https://shop.example.com//{slug}` and bouncing off the
 * server's validator.
 */
function normalizeDomain(raw) {
    return String(raw ?? "")
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/\/+$/, "");
}

/**
 * Compose {mode, domain} back into a template, or null for default mode.
 *
 * Mirrors the server's validation (api/src/endpoints/platform_settings.py
 * `_coerce_store_base_url_template_or_abort`) so a bad domain is caught
 * before the round-trip. Returns an `error` string instead of throwing;
 * the caller surfaces it in the card's error paragraph.
 */
function composeStorefrontTemplate(mode, rawDomain) {
    if (mode === "default") {
        return { value: null };
    }

    const domain = normalizeDomain(rawDomain);
    if (!domain) {
        // Not an error the SA needs shouting at them -- they have just
        // picked a mode and have not typed yet. Simply nothing to save.
        return { value: null, incomplete: true };
    }
    if (/\s/.test(domain)) {
        return { error: "The domain cannot contain spaces." };
    }
    if (/[{}]/.test(domain)) {
        return {
            error:
                "Enter only the domain, such as shop.threadflow.com. " +
                "The store's slug is added automatically.",
        };
    }
    if (domain.includes("/")) {
        return {
            error:
                "Enter only the domain, without a path -- " +
                "for example shop.threadflow.com.",
        };
    }
    // A bare hostname needs at least one dot; `localhost` is not a
    // plausible value for a customer-facing storefront link.
    if (!domain.includes(".")) {
        return { error: "Enter a full domain, such as shop.threadflow.com." };
    }

    return {
        value:
            mode === "subdomain"
                ? `https://{slug}.${domain}`
                : `https://${domain}/{slug}`,
    };
}

export default {
    name: "PlatformSettings",
    components: { PreviewFallbackToolsCard, DtfArtCleanupCard, SupportEmailCard, FundraisingFlagCard, AddNewOrderCard },

    setup() {
        const store = useStore();
        const hideUnpreviewable = ref(false);
        const loading = ref(true);
        const saving = ref(false);
        const errorMsg = ref("");

        const suppressSnackbars = ref(false);
        const snackbarSaving = ref(false);
        const snackbarErrorMsg = ref("");

        // Defaults FALSE -- gates ENFORCED. Matches the backend
        // fail-closed default: on any uncertainty (a failed GET included)
        // the UI must not claim a safety gate is off.
        const disableArtworkGates = ref(false);
        const artworkGatesSaving = ref(false);
        const artworkGatesErrorMsg = ref("");
        const disableStoreGates = ref(false);
        const storeGatesSaving = ref(false);
        const storeGatesErrorMsg = ref("");

        // Defaults FALSE -- the sweep is OFF. Matches the backend
        // fail-closed default. On any uncertainty (a failed GET
        // included) the UI must not claim an unattended ACH debit job
        // is armed, and must not present a stop button that is not
        // connected to anything.
        const netObligationSweepEnabled = ref(false);
        const netObligationSweepSaving = ref(false);
        const netObligationSweepErrorMsg = ref("");
        const billingLapseEnabled = ref(false);
        const billingLapseSaving = ref(false);
        const billingLapseErrorMsg = ref("");

        // Stripe platforms (dual-mode unit 5) -- read-only display.
        // Starts EMPTY, not with a fabricated pair of rows: until the fetch
        // lands we do not know what this deployment is configured for, and
        // inventing "sandbox: configured" would be a claim rather than a
        // placeholder. A failed fetch renders the error instead of an empty
        // list, because silence would read as "no platforms configured" --
        // a different and much more alarming fact than "could not ask".
        const stripePlatforms = ref([]);
        const stripePlatformsErrorMsg = ref("");

        const STRIPE_PLATFORM_LABELS = {
            sandbox: "Sandbox",
            live: "Live",
        };

        function platformLabel(mode) {
            // Fall back to the raw mode rather than blank: a mode this UI
            // does not know about is still one the API reported, and hiding
            // it would be worse than showing an unfamiliar word.
            return STRIPE_PLATFORM_LABELS[mode] || mode;
        }

        async function loadStripePlatforms() {
            stripePlatformsErrorMsg.value = "";
            try {
                const resp = await axios.get("/api/superadmin/stripe-platforms");
                stripePlatforms.value = resp?.data?.platforms || [];
            } catch (e) {
                stripePlatforms.value = [];
                stripePlatformsErrorMsg.value =
                    "Could not load the Stripe platform configuration.";
            }
        }

        const enableInlineImages = ref(false);
        const inlineImagesSaving = ref(false);
        const inlineImagesErrorMsg = ref("");
        // Departments defaults TRUE -- the OPPOSITE of inline images. A
        // failed read must not present the surfaces as disabled (see
        // useEnableDepartments.js for the full rationale).
        const enableDepartments = ref(true);
        const departmentsSaving = ref(false);
        const departmentsErrorMsg = ref("");

        const allowedMethodSlugs = ref([]);
        const knownMethods = ref([]);
        const methodsSaving = ref(false);
        const methodsErrorMsg = ref("");

        // Storefront link address. `storefrontTemplate` is the PERSISTED
        // server truth (a template string, or null). `storefrontMode` and
        // `storefrontDomain` are the UI's derived view of it -- they are
        // parsed FROM it on load and composed back INTO it on save, so the
        // single backend field stays the only source of truth.
        const storefrontTemplate = ref(null);
        const storefrontMode = ref("default");
        const storefrontDomain = ref("");
        const storefrontUnrecognised = ref(false);
        const storefrontSaving = ref(false);
        const storefrontErrorMsg = ref("");

        // Address-validation provider. The backend resolves unset (and any
        // unrecognised stored value) to "shipengine" and never returns null,
        // so this ref always holds one of the two known values -- there is no
        // empty state for the radios to represent.
        const addressProvider = ref(ADDRESS_PROVIDER_DEFAULT);
        const addressProviderSaving = ref(false);
        const addressProviderErrorMsg = ref("");
        const shippingRateProvider = ref(SHIPPING_RATE_PROVIDER_DEFAULT);
        const shippingRateProviderSaving = ref(false);
        const shippingRateProviderErrorMsg = ref("");
        // ShipEngine fallback switches, keyed by SHIPENGINE_SWITCHES[].id.
        // `enabled` is null until the server has answered with a boolean:
        // an unknown switch must never render as a confident "Off" (the
        // rates switch defaults ON, so a false Off misleads in the
        // dangerous direction), and it cannot be toggled.
        const shipengineState = reactive(
            Object.fromEntries(
                SHIPENGINE_SWITCHES.map((sw) => [
                    sw.id,
                    { enabled: null, saving: false, error: "" },
                ])
            )
        );
        const shipengineStateLabel = (enabled) =>
            enabled === null ? "Unknown" : enabled ? "On" : "Off";

        // The back-office mode, plus whether PROD can be selected HERE.
        // `backOfficeProdSelectable` starts FALSE so the prod radio is
        // disabled until the server says otherwise -- during the first
        // paint, before the GET resolves, the safe rendering is the one
        // that cannot be clicked into production.
        const backOfficeMode = ref(BACK_OFFICE_MODE_DEFAULT);
        const backOfficeModeSaving = ref(false);
        const backOfficeModeErrorMsg = ref("");
        const backOfficeProdSelectable = ref(false);
        const backOfficeProdBlockedReason = ref("");

        // The server's whole answer is kept as-is: it is the source of
        // truth for the prefix in force, its source, and whether this
        // environment may store one. The UI computes none of it.
        const poPrefix = ref({});
        const poPrefixLoaded = ref(false);
        const poPrefixInput = ref("");
        const poPrefixSaving = ref(false);
        const poPrefixErrorMsg = ref("");
        const poPrefixSaved = ref(false);

        // Risk Watch thresholds: the server's whole answer is kept (value,
        // default, min, max per field); the inputs are the editable copy.
        const riskWatch = ref({});
        const riskWatchLoaded = ref(false);
        const riskWatchInputs = ref({});
        const riskWatchSaving = ref(false);
        const riskWatchErrorMsg = ref("");
        const riskWatchSaved = ref(false);

        // Size-estimate stitch density: the server's whole answer (value,
        // default, min, max); the input is the editable copy.
        const stitchDensity = ref({});
        const stitchDensityLoaded = ref(false);
        const stitchDensityInput = ref("");
        const stitchDensitySaving = ref(false);
        const stitchDensityErrorMsg = ref("");
        const stitchDensitySaved = ref(false);

        function applyStitchDensity(data) {
            if (!data || !Number.isInteger(data.value)) {
                throw new Error("unexpected stitch density payload");
            }
            stitchDensity.value = data;
            stitchDensityInput.value = data.value;
            stitchDensityLoaded.value = true;
        }

        // Color render delay: the server's whole answer (value, default,
        // min, max); the input is the editable copy.
        const colorRenderDelay = ref({});
        const colorRenderDelayLoaded = ref(false);
        const colorRenderDelayInput = ref("");
        const colorRenderDelaySaving = ref(false);
        const colorRenderDelayErrorMsg = ref("");
        const colorRenderDelaySaved = ref(false);

        function applyColorRenderDelay(data) {
            if (!data || typeof data.value !== "number") {
                throw new Error("unexpected color render delay payload");
            }
            colorRenderDelay.value = data;
            colorRenderDelayInput.value = data.value;
            colorRenderDelayLoaded.value = true;
        }

        // Tech contact email: the server's whole answer (value, default,
        // is_default); the input is the editable copy.
        const techContact = ref({});
        const techContactLoaded = ref(false);
        const techContactInput = ref("");
        const techContactSaving = ref(false);
        const techContactErrorMsg = ref("");
        const techContactSaved = ref(false);

        function applyTechContact(data) {
            if (!data || typeof data.value !== "string") {
                throw new Error("unexpected tech contact payload");
            }
            techContact.value = data;
            techContactInput.value = data.value;
            techContactLoaded.value = true;
        }

        function applyRiskWatch(data) {
            const t = data && data.thresholds;
            if (!t || typeof t !== "object") {
                throw new Error("unexpected risk watch payload");
            }
            riskWatch.value = t;
            riskWatchInputs.value = Object.fromEntries(
                RISK_WATCH_FIELDS.map((f) => [f.field, t[f.field].value])
            );
            riskWatchLoaded.value = true;
        }
        const poPrefixSourceText = computed(() => {
            const p = poPrefix.value || {};
            const where =
                p.environment === "sandbox"
                    ? `sandbox ${p.sandbox_port}`
                    : p.environment || "an unidentified environment";
            if (p.source === "pinned") {
                return `pinned for ${where}`;
            }
            if (p.source === "seeded") {
                return `seeded for this environment (${where})`;
            }
            if (p.source === "setting") {
                return `set on this page (${where})`;
            }
            return "";
        });

        function applyPoPrefix(data) {
            const body = data && typeof data === "object" ? data : {};
            poPrefix.value = { ...body, setting: body.setting ?? null };
            // Start from what is in force; if nothing valid is, offer this
            // environment's default rather than the invalid stored value.
            poPrefixInput.value = poPrefix.value.value || poPrefix.value.default || "";
            poPrefixLoaded.value = true;
        }


        // The resolved URL for a sample slug, live as the SA types. In
        // default mode this mirrors the backend's fallback shape
        // (`<app-host>/shop/<slug>`, see api/src/plugins/mail.py
        // storefront_path_url), so the preview is honest about what an
        // email will actually contain.
        const storefrontPreview = computed(() => {
            if (storefrontUnrecognised.value) {
                return String(storefrontTemplate.value ?? "").replace(
                    "{slug}",
                    SAMPLE_SLUG
                );
            }
            if (storefrontMode.value === "default") {
                const origin =
                    typeof window !== "undefined" && window.location
                        ? window.location.origin
                        : "";
                return `${origin}/shop/${SAMPLE_SLUG}`;
            }
            const domain = normalizeDomain(storefrontDomain.value);
            if (!domain) {
                // Nothing typed yet -- show the shape rather than a
                // half-built URL with an empty host.
                return storefrontMode.value === "subdomain"
                    ? `https://${SAMPLE_SLUG}.<your domain>`
                    : `https://<your domain>/${SAMPLE_SLUG}`;
            }
            return storefrontMode.value === "subdomain"
                ? `https://${SAMPLE_SLUG}.${domain}`
                : `https://${domain}/${SAMPLE_SLUG}`;
        });

        async function load() {
            loading.value = true;
            errorMsg.value = "";
            try {
                const resp = await axios.get(URL);
                hideUnpreviewable.value = resp?.data?.value === true;
            } catch (e) {
                errorMsg.value = "Could not load platform settings.";
            }

            snackbarErrorMsg.value = "";
            try {
                const resp = await axios.get(SUPPRESS_URL);
                suppressSnackbars.value = resp?.data?.value === true;
            } catch (e) {
                snackbarErrorMsg.value = "Could not load snackbar setting.";
            }

            artworkGatesErrorMsg.value = "";
            try {
                const resp = await axios.get(ARTWORK_GATES_URL);
                disableArtworkGates.value = resp?.data?.value === true;
            } catch (e) {
                // Leave the toggle OFF. An unreadable setting must never
                // render as "the gates are off".
                disableArtworkGates.value = false;
                artworkGatesErrorMsg.value =
                    "Could not load the artwork launch-gate setting.";
            }

            storeGatesErrorMsg.value = "";
            try {
                const resp = await axios.get(STORE_GATES_URL);
                disableStoreGates.value = resp?.data?.value === true;
            } catch (e) {
                // Leave the toggle OFF: an unreadable setting must never
                // render as "the store launch gates are off".
                disableStoreGates.value = false;
                storeGatesErrorMsg.value =
                    "Could not load the store launch-gate setting.";
            }

            netObligationSweepErrorMsg.value = "";
            try {
                const resp = await axios.get(NET_OBLIGATION_SWEEP_URL);
                netObligationSweepEnabled.value = resp?.data?.value === true;
            } catch (e) {
                // Leave the toggle OFF. An unreadable setting must never
                // render as "the ACH sweep is on" -- an admin who
                // believes it is armed when it is not merely gets
                // confused, but one who believes it is OFF when it is ON
                // stops watching a job that is debiting bank accounts.
                netObligationSweepEnabled.value = false;
                netObligationSweepErrorMsg.value =
                    "Could not load the ACH sweep setting.";
            }

            billingLapseErrorMsg.value = "";
            try {
                const resp = await axios.get(BILLING_LAPSE_URL);
                billingLapseEnabled.value = resp?.data?.value === true;
            } catch (e) {
                // Leave the toggle OFF: an unreadable setting must never
                // render as a definite "on".
                billingLapseEnabled.value = false;
                billingLapseErrorMsg.value =
                    "Could not load the billing lapse setting.";
            }

            for (const sw of SHIPENGINE_SWITCHES) {
                const st = shipengineState[sw.id];
                st.error = "";
                try {
                    const resp = await axios.get(sw.url);
                    const value = resp?.data?.value;
                    // The server always answers a resolved boolean; anything
                    // else is as unknown as a failed GET.
                    st.enabled = typeof value === "boolean" ? value : null;
                    if (st.enabled === null) st.error = sw.loadError;
                } catch (e) {
                    // UNKNOWN, not Off: the page cannot tell which way the
                    // switch is set (rates default ON since 2026-10-27), so
                    // it says so and disables the toggle until a reload.
                    st.enabled = null;
                    st.error = sw.loadError;
                }
            }

            // Its own try/catch inside loadStripePlatforms, matching every
            // setting above: one unreachable endpoint must not blank the
            // rest of the page.
            await loadStripePlatforms();

            inlineImagesErrorMsg.value = "";
            try {
                const resp = await axios.get(INLINE_IMAGES_URL);
                enableInlineImages.value = resp?.data?.value === true;
            } catch (e) {
                inlineImagesErrorMsg.value =
                    "Could not load the inline-images setting.";
            }

            departmentsErrorMsg.value = "";
            try {
                const resp = await axios.get(DEPARTMENTS_URL);
                // Only a real boolean overrides the TRUE default; any other
                // shape leaves departments visible.
                enableDepartments.value =
                    typeof resp?.data?.value === "boolean"
                        ? resp.data.value
                        : true;
            } catch (e) {
                // Fail SAFE: leave the switch ON rather than misrepresent
                // live departments as disabled.
                enableDepartments.value = true;
                departmentsErrorMsg.value =
                    "Could not load the departments setting.";
            }

            storefrontErrorMsg.value = "";
            try {
                const resp = await axios.get(STOREFRONT_URL);
                const value = resp?.data?.value;
                applyStorefrontTemplate(
                    typeof value === "string" && value ? value : null
                );
            } catch (e) {
                storefrontErrorMsg.value =
                    "Could not load the storefront link address.";
            }

            addressProviderErrorMsg.value = "";
            try {
                const resp = await axios.get(ADDRESS_PROVIDER_URL);
                const value = resp?.data?.value;
                // Adopt only a value we recognise. An unexpected string
                // would leave BOTH radios unchecked, which reads as "no
                // provider is validating addresses" -- alarming and untrue.
                // The backend already fails safe to shipengine; mirror that
                // here rather than rendering a third, empty state.
                addressProvider.value = ADDRESS_PROVIDERS.some(
                    (o) => o.value === value
                )
                    ? value
                    : ADDRESS_PROVIDER_DEFAULT;
            } catch (e) {
                addressProviderErrorMsg.value =
                    "Could not load the address validation provider.";
            }

            shippingRateProviderErrorMsg.value = "";
            try {
                const resp = await axios.get(SHIPPING_RATE_PROVIDER_URL);
                const value = resp?.data?.value;
                // Same rule as the address provider: adopt only a known
                // value, so an unexpected string cannot leave both radios
                // unchecked and imply nobody is quoting rates.
                shippingRateProvider.value = SHIPPING_RATE_PROVIDERS.some(
                    (o) => o.value === value
                )
                    ? value
                    : SHIPPING_RATE_PROVIDER_DEFAULT;
            } catch (e) {
                shippingRateProviderErrorMsg.value =
                    "Could not load the shipping rate provider.";
            }

            backOfficeModeErrorMsg.value = "";
            try {
                const resp = await axios.get(BACK_OFFICE_MODE_URL);
                const value = resp?.data?.value;
                // Adopt only a known value. An unexpected string resolves
                // to STAGE rather than leaving both radios unchecked, and
                // the direction of that fallback is the safe one.
                backOfficeMode.value = BACK_OFFICE_MODES.some(
                    (o) => o.value === value
                )
                    ? value
                    : BACK_OFFICE_MODE_DEFAULT;
                // Trust the server on selectability. The UI cannot compute
                // this -- it depends on PUBLIC_DEPLOYMENT and on which
                // credentials the environment holds, neither of which the
                // browser can see. `=== true` rather than a truthy check so
                // a missing field leaves prod disabled.
                backOfficeProdSelectable.value =
                    resp?.data?.prod_selectable === true;
                backOfficeProdBlockedReason.value =
                    resp?.data?.prod_blocked_reason || "";
            } catch (e) {
                // Leave prod DISABLED on a failed read. We do not know
                // whether this environment may select it, and the
                // asymmetric cost says assume it may not.
                backOfficeProdSelectable.value = false;
                backOfficeModeErrorMsg.value =
                    "Could not load the decorator back office setting.";
            }

            poPrefixErrorMsg.value = "";
            try {
                const resp = await axios.get(PO_PREFIX_URL);
                applyPoPrefix(resp?.data);
            } catch (e) {
                // Leave the controls hidden: without the server's answer we
                // do not know what this environment may store.
                poPrefixLoaded.value = false;
                poPrefixErrorMsg.value =
                    "Could not load the decorator order number prefix.";
            }

            riskWatchErrorMsg.value = "";
            try {
                const resp = await axios.get(RISK_WATCH_URL);
                applyRiskWatch(resp?.data);
            } catch (e) {
                // Without the server's answer we do not know the allowed
                // ranges, so offer no controls.
                riskWatchLoaded.value = false;
                riskWatchErrorMsg.value =
                    "Could not load the Risk Watch thresholds.";
            }

            techContactErrorMsg.value = "";
            try {
                const resp = await axios.get(TECH_CONTACT_URL);
                applyTechContact(resp?.data);
            } catch (e) {
                techContactLoaded.value = false;
                techContactErrorMsg.value = "Could not load the tech contact email.";
            }

            stitchDensityErrorMsg.value = "";
            try {
                const resp = await axios.get(STITCH_DENSITY_URL);
                applyStitchDensity(resp?.data);
            } catch (e) {
                // Without the server's answer we do not know the allowed
                // range, so offer no controls.
                stitchDensityLoaded.value = false;
                stitchDensityErrorMsg.value =
                    "Could not load the estimated stitches per square inch.";
            }

            colorRenderDelayErrorMsg.value = "";
            try {
                const resp = await axios.get(COLOR_RENDER_DELAY_URL);
                applyColorRenderDelay(resp?.data);
            } catch (e) {
                // Without the server's answer we do not know the allowed
                // range, so offer no controls.
                colorRenderDelayLoaded.value = false;
                colorRenderDelayErrorMsg.value =
                    "Could not load the color render delay.";
            }

            methodsErrorMsg.value = "";
            try {
                const [methodsResp, knownResp] = await Promise.all([
                    axios.get(METHODS_URL),
                    axios.get(KNOWN_METHODS_URL),
                ]);
                const value = methodsResp?.data?.value;
                allowedMethodSlugs.value = Array.isArray(value)
                    ? value
                    : ["embroidery", "dtf"];
                knownMethods.value =
                    knownResp?.data?.decoration_processes || [];
            } catch (e) {
                methodsErrorMsg.value =
                    "Could not load the decoration-method allowlist.";
            }

            loading.value = false;
        }

        async function onToggle() {
            // Optimistically flipped by v-model; PATCH the new value and
            // reconcile with the server's returned truth (or revert on
            // failure).
            const desired = hideUnpreviewable.value;
            saving.value = true;
            errorMsg.value = "";
            try {
                const resp = await axios.patch(URL, { value: desired });
                hideUnpreviewable.value = resp?.data?.value === true;
            } catch (e) {
                // Revert the optimistic flip so the switch matches the
                // last-known-good persisted state.
                hideUnpreviewable.value = !desired;
                const status = e?.response?.status;
                if (status === 403) {
                    errorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else {
                    errorMsg.value = "Could not save this setting.";
                }
            } finally {
                saving.value = false;
            }
        }

        async function onSnackbarToggle() {
            const desired = suppressSnackbars.value;
            snackbarSaving.value = true;
            snackbarErrorMsg.value = "";
            try {
                const resp = await axios.patch(SUPPRESS_URL, { value: desired });
                suppressSnackbars.value = resp?.data?.value === true;
                // Sync the in-memory flag in the notification store so the change
                // takes effect immediately without a page reload.
                await store.dispatch('setSuppressErrorSnackbars');
            } catch (e) {
                suppressSnackbars.value = !desired;
                const status = e?.response?.status;
                if (status === 403) {
                    snackbarErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else {
                    snackbarErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                snackbarSaving.value = false;
            }
        }

        async function onInlineImagesToggle() {
            const desired = enableInlineImages.value;
            inlineImagesSaving.value = true;
            inlineImagesErrorMsg.value = "";
            try {
                const resp = await axios.patch(INLINE_IMAGES_URL, {
                    value: desired,
                });
                enableInlineImages.value = resp?.data?.value === true;
            } catch (e) {
                enableInlineImages.value = !desired;
                const status = e?.response?.status;
                if (status === 403) {
                    inlineImagesErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else {
                    inlineImagesErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                inlineImagesSaving.value = false;
            }
        }

        async function onDepartmentsToggle() {
            const desired = enableDepartments.value;
            departmentsSaving.value = true;
            departmentsErrorMsg.value = "";
            try {
                const resp = await axios.patch(DEPARTMENTS_URL, {
                    value: desired,
                });
                enableDepartments.value = resp?.data?.value === true;
            } catch (e) {
                // Roll the optimistic flip back to the last known value.
                enableDepartments.value = !desired;
                const status = e?.response?.status;
                if (status === 403) {
                    departmentsErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else {
                    departmentsErrorMsg.value =
                        "Could not save this setting.";
                }
            } finally {
                departmentsSaving.value = false;
            }
        }

        async function onMethodToggle(slug, event) {
            // The input is a CONTROLLED checkbox (:checked bound to
            // allowedMethodSlugs, not v-model) so it can be reverted on
            // failure. The native click already flipped the DOM
            // element's own .checked property before this handler runs;
            // if we end up NOT changing allowedMethodSlugs (blocked or
            // failed PATCH), Vue's re-render is a no-op on an unchanged
            // binding and will NOT re-force the DOM attribute -- so we
            // explicitly reset event.target.checked back to match the
            // (unchanged) reactive source of truth in both early-exit
            // paths below.
            const target = event.target;
            const checked = target.checked;
            const current = allowedMethodSlugs.value;
            let next;
            if (checked) {
                next = current.includes(slug)
                    ? current
                    : [...current, slug];
            } else {
                next = current.filter((s) => s !== slug);
            }

            // Never attempt to PATCH an empty allowlist -- the backend
            // rejects it (a store with zero pickable methods is a
            // footgun), and the checkbox UI should not even round-trip
            // to the server to find that out.
            if (next.length === 0) {
                methodsErrorMsg.value =
                    "At least one decoration method must stay enabled.";
                target.checked = current.includes(slug);
                return;
            }

            methodsSaving.value = true;
            methodsErrorMsg.value = "";
            try {
                const resp = await axios.patch(METHODS_URL, { value: next });
                const value = resp?.data?.value;
                allowedMethodSlugs.value = Array.isArray(value)
                    ? value
                    : next;
            } catch (e) {
                // Revert -- the reactive source of truth is unchanged,
                // so force the DOM checkbox back to match it.
                target.checked = current.includes(slug);
                const status = e?.response?.status;
                if (status === 403) {
                    methodsErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else {
                    methodsErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                methodsSaving.value = false;
            }
        }

        /**
         * Adopt a persisted template as the new last-known-good state,
         * deriving the mode + domain the controls display.
         */
        function applyStorefrontTemplate(template) {
            storefrontTemplate.value = template;
            const parsed = parseStorefrontTemplate(template);
            if (parsed === null) {
                // Matches neither shape -- render read-only rather than
                // coercing it into a mode we would then overwrite.
                storefrontUnrecognised.value = true;
                storefrontMode.value = "";
                storefrontDomain.value = "";
                return;
            }
            storefrontUnrecognised.value = false;
            storefrontMode.value = parsed.mode;
            storefrontDomain.value = parsed.domain;
        }

        /**
         * Persist the composed template, reverting to the last-known-good
         * state on failure.
         *
         * Save-on-change matches every other card on this page. The
         * `incomplete` case (a custom mode with no domain typed yet) is
         * deliberately a no-op rather than an error: picking a mode is
         * not yet an instruction to save anything.
         */
        async function saveStorefront() {
            const composed = composeStorefrontTemplate(
                storefrontMode.value,
                storefrontDomain.value
            );
            if (composed.error) {
                storefrontErrorMsg.value = composed.error;
                return;
            }
            if (composed.incomplete) {
                storefrontErrorMsg.value = "";
                return;
            }

            const previous = storefrontTemplate.value;
            storefrontSaving.value = true;
            storefrontErrorMsg.value = "";
            try {
                const resp = await axios.patch(STOREFRONT_URL, {
                    value: composed.value,
                });
                // Adopt the server's echo, but ONLY when it is a usable
                // value. A response that omits `value` (or returns a
                // non-string) must not be read as "the setting is now
                // null" -- that would blank the card immediately after a
                // successful save and look to the SA like their change
                // cleared the setting. The write succeeded, so
                // `composed.value` is the truth we just persisted.
                const saved = resp?.data?.value;
                const isUsable = typeof saved === "string" && saved;
                applyStorefrontTemplate(
                    isUsable ? saved : composed.value ?? null
                );
            } catch (e) {
                // Revert the controls to the persisted truth so the page
                // never claims a value the server does not hold.
                applyStorefrontTemplate(previous);
                const status = e?.response?.status;
                if (status === 403) {
                    storefrontErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    storefrontErrorMsg.value =
                        "That address was rejected. Enter a domain such as shop.threadflow.com.";
                } else {
                    storefrontErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                storefrontSaving.value = false;
            }
        }

        async function onStorefrontModeChange(mode) {
            storefrontMode.value = mode;
            storefrontErrorMsg.value = "";
            // An explicit mode click is consent to leave the read-only
            // unrecognised-template state and manage the value here.
            storefrontUnrecognised.value = false;
            if (mode === "default") {
                // Clearing is immediate and unambiguous -- there is no
                // domain to collect first.
                storefrontDomain.value = "";
                await saveStorefront();
            }
            // For the custom modes we wait for a domain; saving now would
            // either no-op or compose a URL with an empty host.
        }

        async function onStorefrontDomainChange() {
            await saveStorefront();
        }

        /**
         * Persist the chosen address-validation provider.
         *
         * Save-on-change matches every other card on this page. Unlike the
         * storefront card there is no second control to wait for, so the
         * click IS the instruction to save.
         *
         * On failure the radio REVERTS to the persisted value. That matters
         * more than the message: a control left showing "ThreadFlow
         * Decorator app" after a failed save would tell the SA that address
         * validation is stubbed platform-wide when it is still running on
         * ShipEngine.
         */
        async function onAddressProviderChange(provider) {
            if (provider === addressProvider.value) {
                // Re-selecting the current value is not a change. Writing it
                // anyway would stamp updated_at / updated_by on a
                // platform-wide setting for nothing and muddy the audit
                // trail.
                return;
            }

            const previous = addressProvider.value;
            addressProvider.value = provider;
            addressProviderSaving.value = true;
            addressProviderErrorMsg.value = "";
            try {
                const resp = await axios.patch(ADDRESS_PROVIDER_URL, {
                    value: provider,
                });
                // Adopt the server's echo when it is one of the known
                // values; otherwise keep what we just successfully wrote.
                // A response that omits `value` must not be read as "the
                // setting is now unset".
                const saved = resp?.data?.value;
                addressProvider.value = ADDRESS_PROVIDERS.some(
                    (o) => o.value === saved
                )
                    ? saved
                    : provider;
            } catch (e) {
                addressProvider.value = previous;
                const status = e?.response?.status;
                if (status === 403) {
                    addressProviderErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    addressProviderErrorMsg.value =
                        "That provider was rejected by the server.";
                } else {
                    addressProviderErrorMsg.value =
                        "Could not save this setting.";
                }
            } finally {
                addressProviderSaving.value = false;
            }
        }

        /**
         * Save the shipping-rate provider, reverting the control on
         * failure so it never shows a provider that is not in effect.
         */
        async function onShippingRateProviderChange(provider) {
            if (provider === shippingRateProvider.value) {
                // Re-selecting the current value is not a change; writing
                // it would stamp updated_at on a platform-wide setting for
                // nothing.
                return;
            }

            const previous = shippingRateProvider.value;
            shippingRateProvider.value = provider;
            shippingRateProviderSaving.value = true;
            shippingRateProviderErrorMsg.value = "";
            try {
                const resp = await axios.patch(SHIPPING_RATE_PROVIDER_URL, {
                    value: provider,
                });
                const saved = resp?.data?.value;
                shippingRateProvider.value = SHIPPING_RATE_PROVIDERS.some(
                    (o) => o.value === saved
                )
                    ? saved
                    : provider;
            } catch (e) {
                shippingRateProvider.value = previous;
                const status = e?.response?.status;
                if (status === 403) {
                    shippingRateProviderErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    shippingRateProviderErrorMsg.value =
                        "That provider was rejected by the server.";
                } else {
                    shippingRateProviderErrorMsg.value =
                        "Could not save this setting.";
                }
            } finally {
                shippingRateProviderSaving.value = false;
            }
        }

        /**
         * Pull the human-readable reason out of an error response.
         *
         * THE SHAPE IS HTML, NOT JSON, and that is not a quirk of this
         * endpoint -- it is platform-wide. Flask's `abort(409, msg)` renders
         * an error PAGE, so `response.data` arrives as a string like
         * `<!doctype html>...<p>the actual reason</p>...`. Verified live on
         * sandbox 8086 against the deployed branch, and cross-checked
         * against a sibling endpoint's 400 (shipping-rate-provider), which
         * does exactly the same.
         *
         * This matters because the alternative is the generic fallback, and
         * the generic fallback cannot tell an operator WHICH of the two
         * refusals they hit -- "this is a sandbox" and "the prod credential
         * is missing" have completely different fixes.
         *
         * Handles the JSON shape too. Some surfaces do return
         * `{message: ...}`, and an endpoint that changed to JSON later
         * should not silently regress this back to the fallback.
         *
         * Returns "" when nothing usable is found, so the caller's `||`
         * fallback still fires rather than printing an empty box.
         */
        function extractServerReason(err) {
            const data = err?.response?.data;
            if (!data) return "";
            if (typeof data === "object") {
                return data.message || data.detail || "";
            }
            if (typeof data !== "string") return "";
            // The <p> Flask puts the abort() description in. Non-greedy so a
            // later paragraph cannot be swallowed into the match.
            const paragraph = data.match(/<p>([\s\S]*?)<\/p>/i);
            const raw = paragraph ? paragraph[1] : data;
            // Strip any remaining tags and decode the entities Flask emits
            // (an apostrophe in our own message becomes &#39;).
            const text = raw
                .replace(/<[^>]*>/g, "")
                .replace(/&#39;/g, "'")
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/\s+/g, " ")
                .trim();
            // A bare "<!doctype html>" with no <p> would yield noise; only
            // return something that looks like a sentence.
            return text.length > 3 ? text : "";
        }

        /**
         * Switch the tf-deconetwork back office, reverting the control on
         * failure so the radio never shows a back office that is not in
         * effect.
         *
         * The revert is the load-bearing part. An SA who believes they are
         * on staging while orders go to production is precisely the failure
         * this whole feature exists to prevent, and a control left showing
         * an optimistic value it failed to save produces exactly that.
         */
        async function onBackOfficeModeChange(mode) {
            if (mode === backOfficeMode.value) {
                // Re-selecting the current value is not a change. Writing it
                // would stamp updated_at on a platform-wide setting AND put
                // a spurious "mode changed" line in the log that a
                // wrong-back-office incident would be investigated from.
                return;
            }

            const previous = backOfficeMode.value;
            backOfficeMode.value = mode;
            backOfficeModeSaving.value = true;
            backOfficeModeErrorMsg.value = "";
            try {
                const resp = await axios.patch(BACK_OFFICE_MODE_URL, {
                    value: mode,
                });
                const saved = resp?.data?.value;
                backOfficeMode.value = BACK_OFFICE_MODES.some(
                    (o) => o.value === saved
                )
                    ? saved
                    : mode;
                if (resp?.data) {
                    backOfficeProdSelectable.value =
                        resp.data.prod_selectable === true;
                    backOfficeProdBlockedReason.value =
                        resp.data.prod_blocked_reason || "";
                }
            } catch (e) {
                backOfficeMode.value = previous;
                const status = e?.response?.status;
                const serverReason = extractServerReason(e);
                if (status === 409) {
                    // The server explains WHICH refusal applies -- a sandbox,
                    // or a missing prod credential -- and those have
                    // different fixes. Surfacing it verbatim is the only way
                    // an operator knows which one to act on.
                    backOfficeModeErrorMsg.value =
                        serverReason ||
                        "This environment cannot select the production back office.";
                } else if (status === 403) {
                    backOfficeModeErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    backOfficeModeErrorMsg.value =
                        "That back office was rejected by the server.";
                } else {
                    backOfficeModeErrorMsg.value =
                        "Could not save this setting.";
                }
            } finally {
                backOfficeModeSaving.value = false;
            }
        }

        /**
         * Save the prefix in the box. The card re-renders from the server's
         * answer; a refusal leaves the prefix in force untouched and shows
         * the server's sentence, which says what is wrong with the value.
         */
        async function onPoPrefixSave(value) {
            const sent = typeof value === "string" ? value.trim() : "";
            poPrefixSaving.value = true;
            poPrefixErrorMsg.value = "";
            poPrefixSaved.value = false;
            try {
                const resp = await axios.patch(PO_PREFIX_URL, { value: sent });
                applyPoPrefix(resp?.data);
                poPrefixSaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                const serverReason = extractServerReason(e);
                if (status === 409) {
                    poPrefixErrorMsg.value =
                        serverReason ||
                        "That prefix cannot be used in this environment.";
                } else if (status === 403) {
                    poPrefixErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    poPrefixErrorMsg.value =
                        serverReason || "That prefix was rejected by the server.";
                } else {
                    poPrefixErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                poPrefixSaving.value = false;
            }
        }

        async function patchRiskWatch(payload) {
            riskWatchSaving.value = true;
            riskWatchErrorMsg.value = "";
            riskWatchSaved.value = false;
            try {
                const resp = await axios.patch(RISK_WATCH_URL, payload);
                applyRiskWatch(resp?.data);
                riskWatchSaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) {
                    riskWatchErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    riskWatchErrorMsg.value =
                        extractServerReason(e) ||
                        "Those numbers were rejected by the server.";
                } else {
                    riskWatchErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                riskWatchSaving.value = false;
            }
        }

        /**
         * Save the four numbers in the boxes. A blank or fractional box is
         * refused here with a sentence; range checks are the server's, and its
         * sentence is shown verbatim.
         */
        async function onRiskWatchSave() {
            const payload = {};
            for (const f of RISK_WATCH_FIELDS) {
                const raw = riskWatchInputs.value[f.field];
                const n = typeof raw === "number" ? raw : Number(raw);
                if (raw === "" || raw === null || !Number.isInteger(n)) {
                    riskWatchSaved.value = false;
                    riskWatchErrorMsg.value = `${f.label} must be a whole number.`;
                    return;
                }
                payload[f.field] = n;
            }
            await patchRiskWatch(payload);
        }

        /** Put every threshold back to its default (null clears the stored value). */
        async function onRiskWatchReset() {
            await patchRiskWatch(
                Object.fromEntries(RISK_WATCH_FIELDS.map((f) => [f.field, null]))
            );
        }

        async function patchStitchDensity(value) {
            stitchDensitySaving.value = true;
            stitchDensityErrorMsg.value = "";
            stitchDensitySaved.value = false;
            try {
                const resp = await axios.patch(STITCH_DENSITY_URL, { value });
                applyStitchDensity(resp?.data);
                stitchDensitySaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) {
                    stitchDensityErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    stitchDensityErrorMsg.value =
                        extractServerReason(e) ||
                        "That number was rejected by the server.";
                } else {
                    stitchDensityErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                stitchDensitySaving.value = false;
            }
        }

        /**
         * Save the number in the box. A blank or fractional box is refused
         * here with a sentence; the range check is the server's, and its
         * sentence is shown verbatim.
         */
        async function onStitchDensitySave() {
            const raw = stitchDensityInput.value;
            const n = typeof raw === "number" ? raw : Number(raw);
            if (raw === "" || raw === null || !Number.isInteger(n)) {
                stitchDensitySaved.value = false;
                stitchDensityErrorMsg.value =
                    "Estimated stitches per square inch must be a whole number.";
                return;
            }
            await patchStitchDensity(n);
        }

        /** Put the default back (null clears the stored value). */
        async function onStitchDensityReset() {
            await patchStitchDensity(null);
        }

        async function patchColorRenderDelay(value) {
            colorRenderDelaySaving.value = true;
            colorRenderDelayErrorMsg.value = "";
            colorRenderDelaySaved.value = false;
            try {
                const resp = await axios.patch(COLOR_RENDER_DELAY_URL, { value });
                applyColorRenderDelay(resp?.data);
                colorRenderDelaySaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) {
                    colorRenderDelayErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 422 || status === 400) {
                    colorRenderDelayErrorMsg.value =
                        extractServerReason(e) ||
                        "That number was rejected by the server.";
                } else {
                    colorRenderDelayErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                colorRenderDelaySaving.value = false;
            }
        }

        /**
         * Save the number in the box. A blank or non-numeric box is refused
         * here with a sentence; the range check is the server's, and its
         * sentence is shown verbatim.
         */
        async function onColorRenderDelaySave() {
            const raw = colorRenderDelayInput.value;
            const n = typeof raw === "number" ? raw : Number(raw);
            if (raw === "" || raw === null || !Number.isFinite(n)) {
                colorRenderDelaySaved.value = false;
                colorRenderDelayErrorMsg.value =
                    "Color render delay must be a number of seconds.";
                return;
            }
            await patchColorRenderDelay(n);
        }

        /** Put the default back (null clears the stored value). */
        async function onColorRenderDelayReset() {
            await patchColorRenderDelay(null);
        }

        async function patchTechContact(value) {
            techContactSaving.value = true;
            techContactErrorMsg.value = "";
            techContactSaved.value = false;
            try {
                const resp = await axios.patch(TECH_CONTACT_URL, { value });
                applyTechContact(resp?.data);
                techContactSaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) {
                    techContactErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 400) {
                    techContactErrorMsg.value =
                        extractServerReason(e) ||
                        "That address was rejected by the server.";
                } else {
                    techContactErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                techContactSaving.value = false;
            }
        }

        /**
         * Save the address in the box. What counts as one plain address is
         * the server's call (it also trims surrounding spaces, as does the
         * email input itself); its sentence is shown verbatim.
         */
        async function onTechContactSave() {
            await patchTechContact(String(techContactInput.value ?? ""));
        }

        /** Put the default back (null clears the stored value). */
        async function onTechContactReset() {
            await patchTechContact(null);
        }

        async function onArtworkGatesToggle() {
            const desired = disableArtworkGates.value;
            artworkGatesSaving.value = true;
            artworkGatesErrorMsg.value = "";
            try {
                const resp = await axios.patch(ARTWORK_GATES_URL, {
                    value: desired,
                });
                disableArtworkGates.value = resp?.data?.value === true;
            } catch (e) {
                // Revert the optimistic flip: the server is the authority
                // on whether a safety gate is off, and a failed write must
                // not leave the UI asserting a bypass that is not active.
                disableArtworkGates.value = !desired;
                const status = e?.response?.status;
                if (status === 403) {
                    artworkGatesErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else {
                    artworkGatesErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                artworkGatesSaving.value = false;
            }
        }

        async function onStoreGatesToggle() {
            const desired = disableStoreGates.value;
            storeGatesSaving.value = true;
            storeGatesErrorMsg.value = "";
            try {
                const resp = await axios.patch(STORE_GATES_URL, {
                    value: desired,
                });
                disableStoreGates.value = resp?.data?.value === true;
            } catch (e) {
                // Revert the optimistic flip: the server is the authority
                // on whether a safety gate is off.
                disableStoreGates.value = !desired;
                const status = e?.response?.status;
                if (status === 403) {
                    storeGatesErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else {
                    storeGatesErrorMsg.value = "Could not save this setting.";
                }
            } finally {
                storeGatesSaving.value = false;
            }
        }

        async function onNetObligationSweepToggle() {
            const desired = netObligationSweepEnabled.value;
            netObligationSweepSaving.value = true;
            netObligationSweepErrorMsg.value = "";
            try {
                const resp = await axios.patch(NET_OBLIGATION_SWEEP_URL, {
                    value: desired,
                });
                netObligationSweepEnabled.value = resp?.data?.value === true;
            } catch (e) {
                // Revert the optimistic flip. The server is the authority
                // on whether the sweep is armed; a failed write must not
                // leave the UI asserting a state the job does not share.
                // That matters in BOTH directions here -- a failed
                // turn-ON that reads as on wastes an admin's attention, a
                // failed turn-OFF that reads as off hides a live debit
                // job from the person trying to stop it.
                netObligationSweepEnabled.value = !desired;
                const status = e?.response?.status;
                if (status === 403) {
                    netObligationSweepErrorMsg.value =
                        "Permission denied. Only Super Admins can change this setting.";
                } else if (status === 409) {
                    // The backend refuses turn-ON until the ACH disclosure
                    // copy names the sweep. Show its reason; fall back to
                    // the same fix in our own words if the body is empty.
                    netObligationSweepErrorMsg.value =
                        extractServerReason(e) ||
                        "The sweep cannot be enabled yet: the ACH authorization disclosure does not mention the sweep's daily debits. Update the disclosure first.";
                } else {
                    netObligationSweepErrorMsg.value =
                        "Could not save this setting.";
                }
            } finally {
                netObligationSweepSaving.value = false;
            }
        }

        async function onBillingLapseToggle() {
            const desired = billingLapseEnabled.value;
            billingLapseSaving.value = true;
            billingLapseErrorMsg.value = "";
            try {
                const resp = await axios.patch(BILLING_LAPSE_URL, {
                    value: desired,
                });
                billingLapseEnabled.value = resp?.data?.value === true;
            } catch (e) {
                // Revert the optimistic flip: the server is the authority.
                billingLapseEnabled.value = !desired;
                billingLapseErrorMsg.value =
                    e?.response?.status === 403
                        ? "Permission denied. Only Super Admins can change this setting."
                        : "Could not save this setting.";
            } finally {
                billingLapseSaving.value = false;
            }
        }

        async function onShipengineSwitchToggle(sw) {
            const st = shipengineState[sw.id];
            const desired = st.enabled;
            st.saving = true;
            st.error = "";
            try {
                const resp = await axios.patch(sw.url, { value: desired });
                st.enabled = resp?.data?.value === true;
            } catch (e) {
                // The server is the authority: revert the optimistic flip.
                st.enabled = !desired;
                st.error =
                    e?.response?.status === 403
                        ? "Permission denied. Only Super Admins can change this setting."
                        : "Could not save this setting.";
            } finally {
                st.saving = false;
            }
        }

        onMounted(load);

        return {
            // Stripe platforms (read-only panel)
            stripePlatforms,
            stripePlatformsErrorMsg,
            platformLabel,
            hideUnpreviewable,
            loading,
            saving,
            errorMsg,
            onToggle,
            suppressSnackbars,
            snackbarSaving,
            snackbarErrorMsg,
            onSnackbarToggle,
            disableArtworkGates,
            artworkGatesSaving,
            artworkGatesErrorMsg,
            onArtworkGatesToggle,
            disableStoreGates,
            storeGatesSaving,
            storeGatesErrorMsg,
            onStoreGatesToggle,
            netObligationSweepEnabled,
            netObligationSweepSaving,
            netObligationSweepErrorMsg,
            onNetObligationSweepToggle,
            billingLapseEnabled,
            billingLapseSaving,
            billingLapseErrorMsg,
            onBillingLapseToggle,
            enableInlineImages,
            inlineImagesSaving,
            inlineImagesErrorMsg,
            enableDepartments,
            departmentsSaving,
            departmentsErrorMsg,
            onDepartmentsToggle,
            onInlineImagesToggle,
            allowedMethodSlugs,
            knownMethods,
            methodsSaving,
            methodsErrorMsg,
            onMethodToggle,
            SAMPLE_SLUG,
            storefrontModes: STOREFRONT_MODES,
            storefrontTemplate,
            storefrontMode,
            storefrontDomain,
            storefrontUnrecognised,
            storefrontSaving,
            storefrontErrorMsg,
            storefrontPreview,
            onStorefrontModeChange,
            onStorefrontDomainChange,
            addressProviders: ADDRESS_PROVIDERS,
            addressProvider,
            addressProviderSaving,
            addressProviderErrorMsg,
            shippingRateProviders: SHIPPING_RATE_PROVIDERS,
            shippingRateProvider,
            shippingRateProviderSaving,
            shippingRateProviderErrorMsg,
            shipengineSwitches: SHIPENGINE_SWITCHES,
            shipengineState,
            onShipengineSwitchToggle,
            shipengineStateLabel,
            backOfficeModes: BACK_OFFICE_MODES,
            BACK_OFFICE_PROD,
            backOfficeMode,
            backOfficeModeSaving,
            backOfficeModeErrorMsg,
            backOfficeProdSelectable,
            backOfficeProdBlockedReason,
            onBackOfficeModeChange,
            poPrefix,
            poPrefixLoaded,
            poPrefixInput,
            poPrefixSaving,
            poPrefixErrorMsg,
            poPrefixSaved,
            RISK_WATCH_FIELDS,
            riskWatch,
            riskWatchLoaded,
            riskWatchInputs,
            riskWatchSaving,
            riskWatchErrorMsg,
            riskWatchSaved,
            onRiskWatchSave,
            onRiskWatchReset,
            stitchDensity,
            stitchDensityLoaded,
            stitchDensityInput,
            stitchDensitySaving,
            stitchDensityErrorMsg,
            stitchDensitySaved,
            onStitchDensitySave,
            onStitchDensityReset,
            colorRenderDelay,
            colorRenderDelayLoaded,
            colorRenderDelayInput,
            colorRenderDelaySaving,
            colorRenderDelayErrorMsg,
            colorRenderDelaySaved,
            onColorRenderDelaySave,
            onColorRenderDelayReset,
            techContact,
            techContactLoaded,
            techContactInput,
            techContactSaving,
            techContactErrorMsg,
            techContactSaved,
            onTechContactSave,
            onTechContactReset,
            poPrefixSourceText,
            onPoPrefixSave,
            onShippingRateProviderChange,
            onAddressProviderChange,
        };
    },
};
</script>

<style scoped>
.sa-settings {
    max-width: 720px;
}
.sa-settings__header {
    margin-bottom: 24px;
}
.sa-settings__title {
    font-size: 22px;
    font-weight: 600;
    color: #1a202c;
    margin: 0 0 6px;
}
.sa-settings__lede {
    margin: 0;
    color: #4b5563;
    line-height: 1.5;
    font-size: 14px;
}
.sa-settings__card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.sa-settings__card-body {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}
.sa-settings__card-copy {
    min-width: 0;
}
.sa-settings__setting-label {
    display: block;
    font-weight: 600;
    color: #1f2937;
    font-size: 15px;
    margin-bottom: 4px;
    cursor: pointer;
}
.sa-settings__setting-help {
    margin: 0;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    max-width: 460px;
}
.sa-settings__not-enforced {
    margin: 14px 0 0;
    padding: 8px 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    color: #92400e;
    font-size: 12px;
    line-height: 1.4;
}
.sa-settings__error {
    margin: 12px 0 0;
    color: #b91c1c;
    font-size: 0.875rem;
}

/* Stripe platforms -- read-only status rows (dual-mode unit 5). */
.sa-settings__platform-list {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.sa-settings__platform-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 12px;
    font-size: 14px;
    color: #1f2937;
}
.sa-settings__platform-name {
    font-weight: 600;
    min-width: 72px;
}
.sa-settings__platform-state {
    color: #4b5563;
}
.sa-settings__platform-count {
    color: #4b5563;
}
/* The warning is a full-width row of its own so it cannot be mistaken for
   another status column when it wraps. */
.sa-settings__platform-row .sa-settings__error {
    flex-basis: 100%;
    margin: 2px 0 0;
}

.sa-settings__method-list {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.sa-settings__method-row {
    display: block;
}
.sa-settings__method-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #1f2937;
    cursor: pointer;
}
.sa-settings__method-checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
}

/* Storefront link address -- radio group + domain field + live preview. */
.sa-settings__mode-list {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.sa-settings__mode-row {
    display: block;
}
.sa-settings__mode-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #1f2937;
    cursor: pointer;
}
.sa-settings__mode-radio {
    width: 16px;
    height: 16px;
    cursor: pointer;
    flex: 0 0 auto;
}
.sa-settings__domain-field {
    margin: 14px 0 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 380px;
}
.sa-settings__domain-label {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
}
.sa-settings__domain-input {
    width: 100%;
    padding: 7px 10px;
    border: 1px solid #cbd2dc;
    border-radius: 6px;
    font-size: 14px;
    color: #1f2937;
    background: #fff;
}
.sa-settings__domain-input:focus-visible {
    outline: 2px solid #2f6df6;
    outline-offset: 1px;
    border-color: #2f6df6;
}
.sa-settings__po-prefix-current {
    margin: 14px 0 0;
    font-size: 14px;
    color: #1f2937;
}
.sa-settings__po-prefix-source {
    margin-left: 8px;
    font-size: 13px;
    color: #6b7280;
}
.sa-settings__po-prefix-row {
    display: flex;
    gap: 8px;
    align-items: center;
}
.sa-settings__button {
    padding: 7px 14px;
    border: 1px solid #2f6df6;
    border-radius: 6px;
    background: #2f6df6;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
}
.sa-settings__button:disabled {
    opacity: 0.6;
    cursor: default;
}
.sa-settings__domain-input:disabled {
    background: #f3f4f6;
    color: #6b7280;
}
.sa-settings__storefront-raw {
    margin: 14px 0 0;
    padding: 8px 12px;
    background: #f3f4f6;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    color: #1f2937;
    word-break: break-all;
}
.sa-settings__storefront-preview {
    margin: 14px 0 0;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.5;
}
.sa-settings__storefront-preview code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    background: #f3f4f6;
    border-radius: 4px;
    padding: 1px 5px;
    word-break: break-all;
}
.sa-settings__storefront-preview-url {
    color: #1f2937;
    font-weight: 600;
}

.sa-settings__nav-link {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border: 1px solid #2f6df6;
    border-radius: 6px;
    background: #fff;
    color: #2f6df6;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;
}
.sa-settings__nav-link:hover {
    background: #2f6df6;
    color: #fff;
}
.sa-settings__nav-link:focus-visible {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
}


/*
 * Toggle switch -- copied from the .sa-toggle pattern in PlansPricing.vue
 * (the is_public toggle). Kept in-component so this page owns its own
 * styling without pulling PlansPricing's full stylesheet.
 */
.sa-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex: 0 0 auto;
}
.sa-toggle__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}
.sa-toggle__track {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    border-radius: 999px;
    background: #cbd2dc;
    transition: background 0.15s ease;
    flex: 0 0 auto;
}
.sa-toggle__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(28, 36, 48, 0.25);
    transition: transform 0.15s ease;
}
.sa-toggle__input:checked + .sa-toggle__track {
    background: #2f6df6;
}
.sa-toggle__input:checked + .sa-toggle__track .sa-toggle__thumb {
    transform: translateX(18px);
}
.sa-toggle__input:focus-visible + .sa-toggle__track {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
}
.sa-toggle__state {
    font-size: 13px;
    color: #5b6572;
}
</style>
